// ======================================================
// REHBER360 — AKILLI PLAN ROBOTU
// SCHEDULER
// ======================================================
//
// Bu katmanın görevi:
//
// - Slot Builder tarafından üretilen günsüz görevleri
//   gerçek hafta ve çalışma günlerine dağıtmak
//
// - Günlük dakika HARD LIMIT'ini korumak
// - Ağır görev limitini korumak
// - Yeni konu sırasını korumak
// - Deneme gününü aşırı yüklememek
// - Aynı dersin aynı güne yığılmasını azaltmak
// - Denemeleri haftalara yaymak
// - Deneme analizini denemeye yakın tutmak
// - Buffer / telafi alanlarını haftalara dağıtmak
//
// Scheduler:
// - Yeni konu üretmez
// - Ders seçmez
// - Müfredat değiştirmez
//
// Bunlar Slot Builder ve Rules katmanlarının görevidir.
// ======================================================

import {
  SMART_PLAN_TASK_TYPES,
  SMART_PLAN_LIMITS,
  SMART_PLAN_CAPACITY_POLICY,
  getSmartPlanDailyMinimumMinutes,
  calculateDailyMinutes,
  calculateHeavyTaskCount,
  validateDailyLoad,
  canPlaceNewTopicOnAssessmentDay,
  getFullMockFrequencyWeeks,
  getBranchMockFrequencyWeeks,
  getWeeklyBufferSlotCount,
  getSubjectPriorityWeight,
  SMART_PLAN_PRIORITY_LEVELS
} from './smartPlanRules';


import {
  getSmartPlanStrategy
} from './smartPlanStrategies';


// ======================================================
// 1. GÖREV GRUPLARI
// ======================================================

const ASSESSMENT_TYPES = [
  SMART_PLAN_TASK_TYPES.FULL_MOCK,
  SMART_PLAN_TASK_TYPES.BRANCH_MOCK,
  SMART_PLAN_TASK_TYPES.TOPIC_SCAN,
  SMART_PLAN_TASK_TYPES.WEEKLY_SCAN
];


const STUDY_TYPES = [
  SMART_PLAN_TASK_TYPES.NEW_TOPIC,
  SMART_PLAN_TASK_TYPES.PRACTICE,
  SMART_PLAN_TASK_TYPES.REVIEW,
  SMART_PLAN_TASK_TYPES.MIXED_PRACTICE,
  SMART_PLAN_TASK_TYPES.ROUTINE
];


// ======================================================
// 2. DENEME Mİ?
// ======================================================

const isAssessmentSlot = slot => {
  return ASSESSMENT_TYPES.includes(
    slot?.type
  );
};


// ======================================================
// 3. ÇALIŞMA SLOTU MU?
// ======================================================

const isStudySlot = slot => {
  return STUDY_TYPES.includes(
    slot?.type
  );
};


// ======================================================
// 4. BOŞ TAKVİM OLUŞTUR
// ======================================================

export const createEmptySmartPlanSchedule = ({
  weeks,
  workingDays = [],
  dailyMinutes
}) => {

  const schedule = [];

  let globalIndex = 0;


  for (
    let weekNumber = 1;
    weekNumber <= Number(weeks || 0);
    weekNumber += 1
  ) {

    const days =
      workingDays.map(
        (day, dayPosition) => {

          const result = {
            weekNumber,
            day,
            dayPosition,
            globalIndex,
            tasks: [],
            dailyMinutes:
              Number(dailyMinutes || 0)
          };

          globalIndex += 1;

          return result;
        }
      );


    schedule.push({
      weekNumber,
      days
    });
  }


  return schedule;
};


// ======================================================
// 5. TAKVİMİ DÜZ LİSTEYE ÇEVİR
// ======================================================

const flattenSchedule = schedule => {

  return schedule.flatMap(
    week => week.days
  );
};


// ======================================================
// 6. GÜNDE DENEME VAR MI?
// ======================================================

const dayHasAssessment = day => {

  return day.tasks.some(
    task =>
      isAssessmentSlot(task)
  );
};


// ======================================================
// 7. GÜNDE TAM DENEME VAR MI?
// ======================================================

const dayHasFullMock = day => {

  return day.tasks.some(
    task =>
      task?.type ===
      SMART_PLAN_TASK_TYPES.FULL_MOCK
  );
};


// ======================================================
// 8. GÜNDE YENİ KONU VAR MI?
// ======================================================

const dayHasNewTopic = day => {

  return day.tasks.some(
    task =>
      task?.type ===
      SMART_PLAN_TASK_TYPES.NEW_TOPIC
  );
};


// ======================================================
// 9. AYNI DERS KAÇ KEZ VAR?
// ======================================================

const countSubjectOnDay = ({
  day,
  subject
}) => {

  if (!subject) {
    return 0;
  }


  return day.tasks.filter(
    task =>
      task?.subject === subject
  ).length;
};



// ======================================================
// 9B. GÜNDE KAÇ FARKLI DERS VAR?
// ======================================================
//
// Burada görev sayısı değil,
// farklı subject sayısı hesaplanır.
//
// Örnek:
//
// Türkçe NEW_TOPIC
// Türkçe PRACTICE
// Matematik REVIEW
//
// = 3 görev
// = 2 farklı ders
//
// Subject taşımayan BUFFER / analiz gibi görevler
// günlük ders çeşitliliğini artırmaz.
// ======================================================

const getDistinctSubjectsOnDay = day => {

  return [
    ...new Set(
      (
        Array.isArray(day?.tasks)
          ? day.tasks
          : []
      )
        .map(
          task =>
            task?.subject
        )
        .filter(Boolean)
    )
  ];
};


// ======================================================
// 10. AYNI KONU AYNI GÜNDE VAR MI?
// ======================================================

const hasSameTopicOnDay = ({
  day,
  slot
}) => {

  if (
    !slot?.subject ||
    !slot?.topic
  ) {
    return false;
  }


  return day.tasks.some(
    task =>
      task?.subject === slot.subject &&
      task?.topic === slot.topic &&
      task?.type === slot.type
  );
};


// ======================================================
// 10B. AYNI KONU AYNI GÜNDE ÇİFT PEKİŞTİRİLİYOR MU?
// ======================================================
//
// Pedagojik sözleşme:
//
// Aynı ders + aynı konu + aynı çalışma gününde:
//
// REVIEW + ROUTINE
//
// birlikte bulunamaz.
//
// Her ikisi ayrı günlerde kullanılabilir.
//
// Bu kural:
//
// NEW_TOPIC + PRACTICE
//
// gibi gerekli öğrenme zincirlerini etkilemez.
// ======================================================

const hasConsolidationOverlapOnDay = ({
  day,
  slot
}) => {

  if (
    !slot?.subject ||
    !slot?.topic
  ) {
    return false;
  }


  const slotIsReview =
    slot?.type ===
      SMART_PLAN_TASK_TYPES.REVIEW;


  const slotIsRoutine =
    slot?.type ===
      SMART_PLAN_TASK_TYPES.ROUTINE;


  if (
    !slotIsReview &&
    !slotIsRoutine
  ) {
    return false;
  }


  const conflictingType =
    slotIsReview
      ? SMART_PLAN_TASK_TYPES.ROUTINE
      : SMART_PLAN_TASK_TYPES.REVIEW;


  return day.tasks.some(
    task =>
      task?.subject === slot.subject &&
      task?.topic === slot.topic &&
      task?.type === conflictingType
  );
};


// ======================================================
// 11. ÖNCEKİ TAKVİM GÜNÜ
// ======================================================

const getPreviousCalendarDay = ({
  allDays,
  day
}) => {

  if (
    !day ||
    day.globalIndex <= 0
  ) {
    return null;
  }


  return (
    allDays[
      day.globalIndex - 1
    ] || null
  );
};


// ======================================================
// 12. BİR SLOT BU GÜNE SIĞAR MI?
// ======================================================

export const canPlaceSmartPlanSlot = ({
  day,
  slot,
  config
}) => {

  if (
    !day ||
    !slot ||
    !config
  ) {
    return false;
  }


  const slotMinutes =
    Number(slot.duration || 0);


  if (
    slotMinutes <= 0
  ) {
    return false;
  }


  // ----------------------------------------------------
// GÜNLÜK GÖREV SAYISI
// ----------------------------------------------------

if (
  day.tasks.length >=
  SMART_PLAN_LIMITS.maxTasksPerDay
) {
  return false;
}


// ----------------------------------------------------
// GÜNLÜK FARKLI DERS HARD LIMIT
// ----------------------------------------------------
//
// maxSubjectsPerDay:
//
// null / undefined
// → otomatik, mevcut scheduler davranışı devam eder.
//
// 2
// → bir günde en fazla 2 farklı ders.
//
// 3
// → bir günde en fazla 3 farklı ders.
//
// Aynı dersten birden fazla görev bu sayıyı
// artırmaz.
//
// Subject taşımayan görevler de sayılmaz.
//
// Önemli:
//
// Bu bir "hedef" değildir.
// maxSubjectsPerDay: 2 demek
// "her gün mutlaka 2 ders olsun" anlamına gelmez.
//
// Yalnızca:
// "2 farklı dersi aşma"
// anlamına gelir.
// ----------------------------------------------------

const maxSubjectsPerDay =
  Number(
    config.maxSubjectsPerDay
  );


if (
  Number.isInteger(
    maxSubjectsPerDay
  ) &&
  maxSubjectsPerDay > 0 &&
  slot?.subject
) {

  const candidateSubjects =
    new Set([
      ...getDistinctSubjectsOnDay(
        day
      ),

      slot.subject
    ]);


  if (
    candidateSubjects.size >
    maxSubjectsPerDay
  ) {
    return false;
  }
}


// ----------------------------------------------------
// AYNI SLOT AYNI GÜNDE TEKRARLANMASIN
// ----------------------------------------------------

 if (
  hasSameTopicOnDay({
    day,
    slot
  })
) {
  return false;
}


// ----------------------------------------------------
// AYNI KONUYA AYNI GÜN REVIEW + ROUTINE YIĞMA
// ----------------------------------------------------
//
// REVIEW ve ROUTINE ayrı ayrı pedagojik olarak
// değerlidir.
//
// Ancak aynı öğrenme birimi için aynı güne
// birlikte yerleştirilmeleri gereksiz çift
// pekiştirme oluşturur.
//
// Scheduler:
// - başka uygun gün varsa orayı kullanır
// - capacity top-up ise başka uygun konu arar
// - sırf süre dolsun diye bu kuralı delmez
// ----------------------------------------------------

if (
  hasConsolidationOverlapOnDay({
    day,
    slot
  })
) {
  return false;
}


  // ----------------------------------------------------
  // BİR GÜNE İKİ DENEME KOYMA
  // ----------------------------------------------------

  if (
    isAssessmentSlot(slot) &&
    dayHasAssessment(day)
  ) {
    return false;
  }


  // ----------------------------------------------------
  // BUFFER DENEME GÜNÜNE KONMASIN
  // ----------------------------------------------------

  if (
    slot.type ===
      SMART_PLAN_TASK_TYPES.BUFFER &&
    dayHasAssessment(day)
  ) {
    return false;
  }


  // ----------------------------------------------------
  // DENEME OLAN GÜNE YENİ KONU KONMASIN
  // ----------------------------------------------------

  if (
    slot.type ===
      SMART_PLAN_TASK_TYPES.NEW_TOPIC &&
    !canPlaceNewTopicOnAssessmentDay({
      dayTasks: day.tasks
    })
  ) {
    return false;
  }


  // ----------------------------------------------------
  // TERS YÖN KONTROLÜ
  //
  // Günün içinde yeni konu zaten varsa sonradan
  // deneme eklenmesine de izin vermiyoruz.
  // ----------------------------------------------------

  if (
    isAssessmentSlot(slot) &&
    dayHasNewTopic(day)
  ) {
    return false;
  }


  // ----------------------------------------------------
  // HARD LOAD VALIDATION
  // ----------------------------------------------------

  const candidateTasks = [
    ...day.tasks,
    slot
  ];


  const loadValidation =
    validateDailyLoad({
      tasks:
        candidateTasks,

      dailyMinutes:
        config.dailyMinutes,

      strategyId:
        config.strategyId
    });


  return loadValidation.valid;
};


// ======================================================
// 13. GÜN PUANLAMA
// ======================================================
//
// Düşük puan = daha iyi gün.
//
// Bu bir "tahmin" sistemi değildir.
// Tamamen deterministik bir yerleştirme skorudur.
// ======================================================

const scoreCandidateDay = ({
  day,
  slot,
  config,
  allDays,
  targetWeek = null,
  targetGlobalIndex = null,
  preferredDayPosition = null
}) => {

  const dailyLimit =
    Number(config.dailyMinutes || 1);


  const usedMinutes =
    calculateDailyMinutes(
      day.tasks
    );


  const utilization =
    usedMinutes /
    dailyLimit;


  let score =
    utilization * 100;


  // ----------------------------------------------------
  // GÖREV SAYISI
  // ----------------------------------------------------

  score +=
    day.tasks.length * 5;


  // ----------------------------------------------------
  // HEDEF HAFTAYA YAKINLIK
  // ----------------------------------------------------

  if (
    Number.isInteger(targetWeek)
  ) {

    score +=
      Math.abs(
        day.weekNumber -
        targetWeek
      ) * 80;
  }


  // ----------------------------------------------------
  // HEDEF TAKVİM NOKTASINA YAKINLIK
  // ----------------------------------------------------

  if (
    Number.isInteger(
      targetGlobalIndex
    )
  ) {

    score +=
      Math.abs(
        day.globalIndex -
        targetGlobalIndex
      ) * 4;
  }


  // ----------------------------------------------------
  // TERCİH EDİLEN HAFTA GÜNÜ
  // ----------------------------------------------------

  if (
    Number.isInteger(
      preferredDayPosition
    )
  ) {

    score +=
      Math.abs(
        day.dayPosition -
        preferredDayPosition
      ) * 8;
  }


  // ----------------------------------------------------
  // AYNI DERSİ AYNI GÜNE YIĞMA
  // ----------------------------------------------------

  const sameSubjectCount =
    countSubjectOnDay({
      day,
      subject:
        slot.subject
    });


  score +=
    sameSubjectCount * 35;


  // ----------------------------------------------------
  // ÖNCEKİ ÇALIŞMA GÜNÜNDE AYNI DERS
  // ----------------------------------------------------

  const previousDay =
    getPreviousCalendarDay({
      allDays,
      day
    });


  if (
    previousDay &&
    slot.subject &&
    previousDay.tasks.some(
      task =>
        task?.subject ===
        slot.subject
    )
  ) {

    score += 12;
  }


  // ----------------------------------------------------
  // DENEME GÜNÜNE EK ÇALIŞMA EKLEMEYİ AZALT
  // ----------------------------------------------------

  if (
    dayHasAssessment(day) &&
    slot.type !==
      SMART_PLAN_TASK_TYPES.MOCK_ANALYSIS
  ) {

    score += 30;
  }


  // ----------------------------------------------------
  // ANALİZİ TAM DENEMEYLE AYNI GÜNE ÇEK
  // ----------------------------------------------------

  if (
    slot.type ===
      SMART_PLAN_TASK_TYPES.MOCK_ANALYSIS &&
    dayHasFullMock(day)
  ) {

    score -= 120;
  }


  // ----------------------------------------------------
  // BUFFER'I DOLU GÜNE YIĞMA
  // ----------------------------------------------------

  if (
    slot.type ===
      SMART_PLAN_TASK_TYPES.BUFFER
  ) {

    score +=
      day.tasks.length * 15;
  }


  // ----------------------------------------------------
  // TAM DENEME MÜMKÜNSE DAHA SADE BİR GÜNE GELSİN
  // ----------------------------------------------------

  if (
    slot.type ===
      SMART_PLAN_TASK_TYPES.FULL_MOCK
  ) {

    score +=
      day.tasks.length * 20;
  }


  // ----------------------------------------------------
  // EŞİTLİK DURUMUNDA ERKEN GÜN KAZANSIN
  // ----------------------------------------------------

  score +=
    day.globalIndex * 0.001;


  return score;
};


// ======================================================
// 14. EN İYİ GÜNÜ BUL
// ======================================================

const findBestDayForSlot = ({
  allDays,
  slot,
  config,
  targetWeek = null,
  targetGlobalIndex = null,
  preferredDayPosition = null,
  notBeforeGlobalIndex = null,
  excludedGlobalIndexes = []
}) => {

  const candidates =
    allDays
      .filter(day => {

        if (
  Number.isInteger(
    notBeforeGlobalIndex
  ) &&
  day.globalIndex <
    notBeforeGlobalIndex
) {
  return false;
}


// Repair sırasında bir görevi,
// az önce çıkarıldığı güne yeniden koymayı engeller.
if (
  Array.isArray(
    excludedGlobalIndexes
  ) &&
  excludedGlobalIndexes.includes(
    day.globalIndex
  )
) {
  return false;
}


return canPlaceSmartPlanSlot({
  day,
  slot,
  config
});
      })
      .map(day => ({
        day,

        score:
          scoreCandidateDay({
            day,
            slot,
            config,
            allDays,
            targetWeek,
            targetGlobalIndex,
            preferredDayPosition
          })
      }))
      .sort((a, b) => {

  // --------------------------------------------------
  // YENİ KONU:
  // Öncelikle plan üzerindeki hedef noktasına
  // en yakın uygun gün seçilsin.
  //
  // Böylece daha boş bir gün bulmak uğruna
  // konu gereksiz biçimde haftalar sonrasına
  // sürüklenmez.
  // --------------------------------------------------

  if (
    slot.type ===
      SMART_PLAN_TASK_TYPES.NEW_TOPIC &&
    Number.isInteger(
      targetGlobalIndex
    )
  ) {

    const distanceA =
      Math.abs(
        a.day.globalIndex -
        targetGlobalIndex
      );

    const distanceB =
      Math.abs(
        b.day.globalIndex -
        targetGlobalIndex
      );


    if (
      distanceA !== distanceB
    ) {
      return (
        distanceA -
        distanceB
      );
    }
  }


  // --------------------------------------------------
  // PRACTICE / REVIEW / ROUTINE:
  // Uygun günler arasında kapasiteyi en iyi
  // dolduran günü tercih et.
  //
  // Örn. 45 dk görev:
  //
  // 75 dk dolu gün → tam 120 dk olur.
  // 30 dk dolu gün → 75 dk olur.
  //
  // Öncelik 75 dk dolu gündedir.
  //
  // Böylece küçük boşlukların plan boyunca
  // parçalanması azaltılır.
  // --------------------------------------------------

  if (
    isStudySlot(slot) &&
    slot.type !==
      SMART_PLAN_TASK_TYPES.NEW_TOPIC
  ) {

    const dailyLimit =
      Number(
        config.dailyMinutes || 0
      );


    const remainingA =
      dailyLimit -
      (
        calculateDailyMinutes(
          a.day.tasks
        ) +
        Number(
          slot.duration || 0
        )
      );


    const remainingB =
      dailyLimit -
      (
        calculateDailyMinutes(
          b.day.tasks
        ) +
        Number(
          slot.duration || 0
        )
      );


    if (
      remainingA !==
      remainingB
    ) {
      return (
        remainingA -
        remainingB
      );
    }
  }


  // --------------------------------------------------
  // DİĞER DURUMLAR:
  // Mevcut genel puanlama sistemi devam eder.
  // --------------------------------------------------

  if (
    a.score !== b.score
  ) {
    return a.score - b.score;
  }


  // Tam eşitlikte erken gün deterministik olarak kazanır.
  return (
    a.day.globalIndex -
    b.day.globalIndex
  );
});


  return (
    candidates[0]?.day ||
    null
  );
};


// ======================================================
// 15. SLOTU GÜNE YERLEŞTİR
// ======================================================

const placeSlotOnDay = ({
  day,
  slot,
  scheduleOrder
}) => {

  const scheduledSlot = {
    ...slot,

    scheduled: true,

    weekNumber:
      day.weekNumber,

    day:
      day.day,

    dayPosition:
      day.dayPosition,

    globalDayIndex:
      day.globalIndex,

    scheduleOrder
  };


  day.tasks.push(
    scheduledSlot
  );


  return scheduledSlot;
};


// ======================================================
// 16. YERLEŞTİRİLEMEYEN SLOT
// ======================================================

const createUnscheduledSlot = ({
  slot,
  reason
}) => {

  return {
    ...slot,

    scheduled: false,

    unscheduledReason:
      reason
  };
};


// ======================================================
// 17. SLOT GÜNLÜK LİMİTTEN BÜYÜK MÜ?
// ======================================================

const resolveUnscheduledReason = ({
  slot,
  config
}) => {

  if (
    Number(slot.duration || 0) >
    Number(config.dailyMinutes || 0)
  ) {

    return (
      'SLOT_EXCEEDS_DAILY_LIMIT'
    );
  }


  return 'NO_FEASIBLE_DAY';
};


// ======================================================
// 18. BRANŞ DENEMESİ DERS SEÇİMİ
// ======================================================
//
// Slot Builder branş denemesini genel bir slot olarak
// üretir.
//
// Scheduler onu seçilen derslerden birine bağlar.
//
// Öncelikli derslerin daha sık branş denemesi alması için
// "atanan deneme sayısı / ders ağırlığı" oranı kullanılır.
// ======================================================

const chooseBranchMockSubject = ({
  config,
  assignmentCounts
}) => {

  const subjects =
    Array.isArray(
      config.selectedSubjects
    )
      ? config.selectedSubjects
      : [];


  if (
    subjects.length === 0
  ) {
    return null;
  }


  const candidates =
    subjects.map(
      (subject, index) => {

        const priority =
          config.subjectPriorities
            ?.[subject] ||
          SMART_PLAN_PRIORITY_LEVELS
            .NORMAL;


        const weight =
          getSubjectPriorityWeight(
            priority
          );


        const count =
          Number(
            assignmentCounts[
              subject
            ] || 0
          );


        return {
          subject,
          index,
          weight,
          ratio:
            count /
            weight
        };
      }
    );


  candidates.sort(
    (a, b) => {

      if (
        a.ratio !== b.ratio
      ) {
        return (
          a.ratio -
          b.ratio
        );
      }


      if (
        a.weight !== b.weight
      ) {
        return (
          b.weight -
          a.weight
        );
      }


      return (
        a.index -
        b.index
      );
    }
  );


  const selected =
    candidates[0];


  if (!selected) {
    return null;
  }


  assignmentCounts[
    selected.subject
  ] =
    Number(
      assignmentCounts[
        selected.subject
      ] || 0
    ) + 1;


  return selected.subject;
};


// ======================================================
// 19. ÇALIŞMA SLOTU SIRASINI DENGELE
// ======================================================
//
// Slot Builder önce bütün NEW_TOPIC'leri,
// sonra PRACTICE'leri,
// sonra REVIEW'leri üretir.
//
// Scheduler bunu olduğu gibi kullansaydı:
//
// İlk haftalar = sadece yeni konu
// Son haftalar = sadece tekrar
//
// gibi kötü bir sonuç oluşabilirdi.
//
// Bu fonksiyon görev türlerini plan boyunca
// deterministik biçimde iç içe geçirir.
// ======================================================

export const interleaveStudySlots = slots => {

  const buckets = {};


  slots.forEach(slot => {

    if (!buckets[slot.type]) {

      buckets[slot.type] = {
        items: [],
        originalMinutes: 0,
        consumedMinutes: 0
      };
    }


    buckets[
      slot.type
    ].items.push(slot);


    buckets[
      slot.type
    ].originalMinutes +=
      Number(
        slot.duration || 0
      );
  });


  const typePreference = [
    SMART_PLAN_TASK_TYPES.NEW_TOPIC,
    SMART_PLAN_TASK_TYPES.PRACTICE,
    SMART_PLAN_TASK_TYPES.REVIEW,
    SMART_PLAN_TASK_TYPES.MIXED_PRACTICE,
    SMART_PLAN_TASK_TYPES.ROUTINE
  ];


  const result = [];


  while (
    Object.values(
      buckets
    ).some(
      bucket =>
        bucket.items.length > 0
    )
  ) {

    const availableTypes =
      typePreference.filter(
        type =>
          buckets[type]?.items
            ?.length > 0
      );


    if (
      availableTypes.length === 0
    ) {
      break;
    }


    availableTypes.sort(
      (a, b) => {

        const bucketA =
          buckets[a];

        const bucketB =
          buckets[b];


        const progressA =
          bucketA.originalMinutes > 0
            ? (
                bucketA
                  .consumedMinutes /
                bucketA
                  .originalMinutes
              )
            : 1;


        const progressB =
          bucketB.originalMinutes > 0
            ? (
                bucketB
                  .consumedMinutes /
                bucketB
                  .originalMinutes
              )
            : 1;


        if (
          progressA !== progressB
        ) {
          return (
            progressA -
            progressB
          );
        }


        return (
          typePreference.indexOf(a) -
          typePreference.indexOf(b)
        );
      }
    );


    const selectedType =
      availableTypes[0];


    const slot =
      buckets[
        selectedType
      ].items.shift();


    buckets[
      selectedType
    ].consumedMinutes +=
      Number(
        slot.duration || 0
      );


    result.push(slot);
  }


  return result;
};


// ======================================================
// 20. HAFTALIK BUFFER HEDEFİ
// ======================================================

const resolveBufferTargetWeek = ({
  bufferIndex,
  weeklyBufferCount,
  totalWeeks
}) => {

  if (
    weeklyBufferCount <= 0
  ) {
    return null;
  }


  const week =
    Math.floor(
      bufferIndex /
      weeklyBufferCount
    ) + 1;


  return Math.min(
    totalWeeks,
    Math.max(1, week)
  );
};


// ======================================================
// 21. DENEME HEDEF HAFTASI
// ======================================================

const resolveAssessmentTargetWeek = ({
  assessmentIndex,
  frequencyWeeks,
  totalWeeks
}) => {

  if (
    frequencyWeeks <= 0
  ) {

    const proportional =
      Math.ceil(
        (
          (assessmentIndex + 1) /
          Math.max(
            1,
            totalWeeks
          )
        ) *
        totalWeeks
      );


    return Math.max(
      1,
      Math.min(
        totalWeeks,
        proportional
      )
    );
  }


  return Math.max(
    1,
    Math.min(
      totalWeeks,
      (
        assessmentIndex + 1
      ) *
      frequencyWeeks
    )
  );
};


// ======================================================
// 22. SONUÇ TAKVİMİNİ TEMİZLE
// ======================================================

const finalizeSchedule = ({
  schedule,
  config
}) => {

  return schedule.map(
    week => ({

      weekNumber:
        week.weekNumber,

      days:
        week.days.map(
          day => {

            const totalMinutes =
              calculateDailyMinutes(
                day.tasks
              );


            const heavyTaskCount =
              calculateHeavyTaskCount(
                day.tasks
              );


            return {
              weekNumber:
                day.weekNumber,

              day:
                day.day,

              tasks:
                [...day.tasks]
                  .sort(
                    (a, b) =>
                      a.scheduleOrder -
                      b.scheduleOrder
                  ),

              totalMinutes,

              remainingMinutes:
                Math.max(
                  0,
                  Number(
                    config.dailyMinutes ||
                    0
                  ) -
                  totalMinutes
                ),

              heavyTaskCount,

              taskCount:
                day.tasks.length
            };
          }
        )
    })
  );
};


// ======================================================
// 23. ANA SCHEDULER
// ======================================================
//
// Kullanım:
//
// const slotResult = buildSmartPlanSlots(config);
//
// const scheduleResult = scheduleSmartPlanSlots({
//   slots: slotResult.slots,
//   config
// });
//
// ======================================================

export const scheduleSmartPlanSlots = ({
  slots = [],
  config
}) => {

  // ----------------------------------------------------
  // TEMEL KONTROL
  // ----------------------------------------------------

  if (
    !config ||
    !Array.isArray(slots)
  ) {

    return {
      success: false,
      schedule: [],
      scheduledSlots: [],
      unscheduledSlots: [],
      warnings: [
        {
          code:
            'INVALID_SCHEDULER_INPUT'
        }
      ],
      summary: null
    };
  }


  if (
    slots.length === 0
  ) {

    return {
      success: true,
      schedule:
        createEmptySmartPlanSchedule({
          weeks:
            config.weeks,

          workingDays:
            config.workingDays || [],

          dailyMinutes:
            config.dailyMinutes
        }),

      scheduledSlots: [],

      unscheduledSlots: [],

      warnings: [
        {
          code:
            'NO_SLOTS_TO_SCHEDULE'
        }
      ],

      summary: {
        totalSlots: 0,
        scheduledCount: 0,
        unscheduledCount: 0
      }
    };
  }


  // ----------------------------------------------------
  // BOŞ TAKVİM
  // ----------------------------------------------------

  const schedule =
    createEmptySmartPlanSchedule({
      weeks:
        config.weeks,

      workingDays:
        config.workingDays || [],

      dailyMinutes:
        config.dailyMinutes
    });


  const allDays =
    flattenSchedule(
      schedule
    );


    const scheduledSlots = [];

  const unscheduledSlots = [];

  const supplementalSlots = [];

  const warnings = [];

  let scheduleOrder = 1;


  // ====================================================
  // A. TAM DENEMELER
  // ====================================================

  const fullMockSlots =
    slots.filter(
      slot =>
        slot.type ===
        SMART_PLAN_TASK_TYPES
          .FULL_MOCK
    );


  const fullMockFrequency =
    getFullMockFrequencyWeeks(
      config.strategyId
    );


const scheduledFullMocks = [];


// ----------------------------------------------------
// FULL_MOCK SONRASI ANALİZ İÇİN GÜN REZERVASYONU
// ----------------------------------------------------
//
// Eğer ilgili MOCK_ANALYSIS gerçekten üretilmişse ve:
//
// FULL_MOCK + MOCK_ANALYSIS > günlük kapasite
//
// ise ikisi aynı güne sığamaz.
//
// Bu durumda FULL_MOCK'u haftanın son çalışma gününe
// itmek yerine sondan bir önceki günü tercih ediyoruz.
//
// Böylece özellikle planın son haftasındaki deneme
// analizsiz kalmaz.
//
// Bu bir hard reservation değildir.
// Yalnız FULL_MOCK için tercih edilen gün konumunu
// bir gün erkene çeker.
//
// Analiz aynı güne sığabiliyorsa mevcut davranış
// aynen korunur.
// ----------------------------------------------------

const generatedMockAnalysisSlots =
  slots.filter(
    slot =>
      slot?.type ===
        SMART_PLAN_TASK_TYPES
          .MOCK_ANALYSIS
  );


fullMockSlots.forEach(
  (slot, index) => {

    const targetWeek =
      resolveAssessmentTargetWeek({
        assessmentIndex:
          index,

        frequencyWeeks:
          fullMockFrequency,

        totalWeeks:
          Number(config.weeks)
      });


    const relatedAnalysisSlot =
      generatedMockAnalysisSlots[
        index
      ] || null;


const requiresSeparateAnalysisDay =
  Boolean(
    relatedAnalysisSlot
  ) &&
  (
    Number(
      slot?.duration || 0
    ) +
    Number(
      relatedAnalysisSlot
        ?.duration || 0
    )
  ) >
  Number(
    config.dailyMinutes || 0
  );


const isFinalPlanWeek =
  Number(
    targetWeek
  ) ===
  Number(
    config.weeks
  );


const shouldReserveAnalysisDay =
  requiresSeparateAnalysisDay &&
  isFinalPlanWeek;


const preferredDayPosition =
  Math.max(
    0,
    (
      config.workingDays
        ?.length || 1
    ) -
    (
      shouldReserveAnalysisDay
        ? 2
        : 1
    )
  );

      const day =
        findBestDayForSlot({
          allDays,
          slot,
          config,
          targetWeek,
          preferredDayPosition
        });


      if (!day) {

        unscheduledSlots.push(
          createUnscheduledSlot({
            slot,

            reason:
              resolveUnscheduledReason({
                slot,
                config
              })
          })
        );

        return;
      }


      const placed =
        placeSlotOnDay({
          day,
          slot,
          scheduleOrder
        });


      scheduleOrder += 1;

      scheduledSlots.push(
        placed
      );

      scheduledFullMocks[
  index
] = placed;
    }
  );


  // ====================================================
  // B. DENEME ANALİZLERİ
  // ====================================================

  const analysisSlots =
    slots.filter(
      slot =>
        slot.type ===
        SMART_PLAN_TASK_TYPES
          .MOCK_ANALYSIS
    );


  analysisSlots.forEach(
    (slot, index) => {

     const relatedMock =
  scheduledFullMocks[
    index
  ];


// ----------------------------------------------------
// ORPHAN MOCK_ANALYSIS KORUMASI
// ----------------------------------------------------
//
// İlgili FULL_MOCK takvime giremediyse,
// bu analizin takvimde tek başına anlamı yoktur.
//
// Analiz slotu generated olarak kalabilir;
// fakat scheduled / unscheduled görev haline
// getirilmez.
//
// Böylece:
// - yapılmamış denemenin analizi görünmez
// - sonraki analizler yanlış full mock'a kaymaz
// ----------------------------------------------------

if (!relatedMock) {

  unscheduledSlots.push(
    createUnscheduledSlot({
      slot,

      reason:
        'DEPENDENCY_NOT_SCHEDULED'
    })
  );

  return;
}


const targetGlobalIndex =
  relatedMock
    ?.globalDayIndex ??
  null;


      const targetWeek =
        relatedMock
          ?.weekNumber ??
        null;


      const notBeforeGlobalIndex =
        targetGlobalIndex;


      const day =
        findBestDayForSlot({
          allDays,
          slot,
          config,
          targetWeek,
          targetGlobalIndex,
          notBeforeGlobalIndex
        });


      if (!day) {

        unscheduledSlots.push(
          createUnscheduledSlot({
            slot,

            reason:
              resolveUnscheduledReason({
                slot,
                config
              })
          })
        );

        return;
      }


      const placed =
        placeSlotOnDay({
          day,
          slot,
          scheduleOrder
        });


      scheduleOrder += 1;

      scheduledSlots.push(
        placed
      );
    }
  );


  // ====================================================
  // C. BRANŞ DENEMELERİ
  // ====================================================

  const branchMockSlots =
    slots.filter(
      slot =>
        slot.type ===
        SMART_PLAN_TASK_TYPES
          .BRANCH_MOCK
    );


  const branchMockFrequency =
    getBranchMockFrequencyWeeks(
      config.strategyId
    );


  const branchAssignmentCounts = {};


  branchMockSlots.forEach(
    (originalSlot, index) => {

      const subject =
        originalSlot.subject ||
        chooseBranchMockSubject({
          config,
          assignmentCounts:
            branchAssignmentCounts
        });


      const slot = {
        ...originalSlot,
        subject
      };


      const targetWeek =
        resolveAssessmentTargetWeek({
          assessmentIndex:
            index,

          frequencyWeeks:
            branchMockFrequency,

          totalWeeks:
            Number(config.weeks)
        });


      const preferredDayPosition =
        Math.max(
          0,
          (
            config.workingDays
              ?.length || 1
          ) - 1
        );


      const day =
        findBestDayForSlot({
          allDays,
          slot,
          config,
          targetWeek,
          preferredDayPosition
        });


      if (!day) {

        unscheduledSlots.push(
          createUnscheduledSlot({
            slot,

            reason:
              resolveUnscheduledReason({
                slot,
                config
              })
          })
        );

        return;
      }


      const placed =
        placeSlotOnDay({
          day,
          slot,
          scheduleOrder
        });


      scheduleOrder += 1;

      scheduledSlots.push(
        placed
      );
    }
  );


  // ====================================================
  // D. BUFFER / TELAFİ SLOTLARI
  // ====================================================

  const bufferSlots =
    slots.filter(
      slot =>
        slot.type ===
        SMART_PLAN_TASK_TYPES
          .BUFFER
    );


  const weeklyBufferCount =
    getWeeklyBufferSlotCount(
      config.strategyId
    );


  bufferSlots.forEach(
    (slot, index) => {

      const targetWeek =
        resolveBufferTargetWeek({
          bufferIndex:
            index,

          weeklyBufferCount,

          totalWeeks:
            Number(config.weeks)
        });


      const preferredDayPosition =
        Math.max(
          0,
          (
            config.workingDays
              ?.length || 1
          ) - 1
        );


      const day =
        findBestDayForSlot({
          allDays,
          slot,
          config,
          targetWeek,
          preferredDayPosition
        });


      if (!day) {

        unscheduledSlots.push(
          createUnscheduledSlot({
            slot,

            reason:
              resolveUnscheduledReason({
                slot,
                config
              })
          })
        );

        return;
      }


      const placed =
        placeSlotOnDay({
          day,
          slot,
          scheduleOrder
        });


      scheduleOrder += 1;

      scheduledSlots.push(
        placed
      );
    }
  );

// ====================================================
// E. NORMAL ÇALIŞMA SLOTLARI
// ====================================================
//
// ÖNEMLİ YERLEŞTİRME PRENSİBİ:
//
// 1. Önce NEW_TOPIC slotları plan boyunca dağıtılır.
//    Çünkü bunlar daha uzun ve ağır görevlerdir.
//
// 2. Practice / Review gibi daha esnek görevler
//    kalan boşlukları doldurur.
//
// Böylece toplam kapasite yeterli olmasına rağmen
// 30-45 dakikalık görevlerin günleri parçalayarak
// 60 dakikalık yeni konulara yer bırakmaması engellenir.
// ====================================================


// ====================================================
// E1. YENİ KONULARI DERSLER ARASINDA DENGELE
// ====================================================
//
// Slot Builder yeni konuları ders ders üretir.
//
// Örn:
// Matematik 1
// Matematik 2
// Matematik 3
// Türkçe 1
// Türkçe 2
//
// Burada derslerin kendi konu sırasını bozmadan:
//
// Matematik 1
// Türkçe 1
// Matematik 2
// Türkçe 2
// Matematik 3
//
// şeklinde round-robin dağıtıyoruz.
// ====================================================

const rawNewTopicSlots =
  slots.filter(
    slot =>
      slot.type ===
      SMART_PLAN_TASK_TYPES.NEW_TOPIC
  );


const newTopicQueues = [];

const newTopicQueueMap =
  new Map();


rawNewTopicSlots.forEach(slot => {

  const key =
    slot.subject ||
    '__unknown_subject__';


  if (
    !newTopicQueueMap.has(key)
  ) {

   const queue = {

  subject:
    key,

  priorityWeight:
    Math.max(
      1,
      Number(
        slot?.priorityWeight || 1
      )
    ),

  originalOrder:
    newTopicQueues.length,

  items:
    []
};


    newTopicQueueMap.set(
      key,
      queue
    );


    newTopicQueues.push(
      queue
    );
  }


  newTopicQueueMap
    .get(key)
    .items
    .push(slot);
});



// ----------------------------------------------------
// NEW_TOPIC — DERS ÖNCELİĞİNE DUYARLI QUEUE SIRASI
// ----------------------------------------------------
//
// Ders içindeki konu sırası DEĞİŞMEZ.
//
// Yalnız dersler arası round-robin başlangıç sırası:
//
// VERY_HIGH
//   ↓
// PRIORITY
//   ↓
// NORMAL
//
// olacak şekilde düzenlenir.
//
// Eşit ağırlıktaki derslerde mevcut üretim sırası
// korunur.
//
// Böylece öncelik bilgisi:
//
// - yeni konu kapsamını silmez,
// - normal dersleri plan dışına atmaz,
// - aynı dersin konu sırasını bozmaz,
// - yalnızca daha öncelikli dersin eşit koşullarda
//   daha erken açılmasını sağlar.
//
// Deterministik davranış korunur.
// ----------------------------------------------------

// ----------------------------------------------------
// KISA PLAN — ÖNCELİĞE DUYARLI NEW_TOPIC QUEUE SIRASI
// ----------------------------------------------------
//
// Dar 1 haftalık planlarda:
//
// VERY_HIGH / PRIORITY dersin ağırlıklı bütçesi
// mevcut olsa bile, ders geç açılırsa reinforcement
// fırsatları azalabilir.
//
// Bu nedenle yalnız 1 haftalık planlarda
// NEW_TOPIC round-robin başlangıç sırası priority
// ağırlığına göre düzenlenir.
//
// 2+ haftalık planlarda eski deterministik sıra
// korunur.
//
// Neden?
//
// Uzun planlarda ders önceliği zaten:
//
// - weighted core budget
// - practice / review dağılımı
// - top-up ağırlığı
//
// üzerinden görünürdür.
//
// Global NEW_TOPIC queue sıralaması uzun Başlangıç
// planlarında pacing / kapasite dengesini bozabilir.
// ----------------------------------------------------

const shouldPrioritizeNewTopicQueueOrder =
  Number(
    config?.weeks || 1
  ) === 1;


if (
  shouldPrioritizeNewTopicQueueOrder
) {

  newTopicQueues.sort(
    (a, b) => {

      const weightDifference =
        Number(
          b?.priorityWeight || 1
        ) -
        Number(
          a?.priorityWeight || 1
        );


      if (
        weightDifference !== 0
      ) {
        return weightDifference;
      }


      return (
        Number(
          a?.originalOrder || 0
        ) -
        Number(
          b?.originalOrder || 0
        )
      );
    }
  );
}


const newTopicSlots = [];

let hasRemainingNewTopics =
  true;


while (
  hasRemainingNewTopics
) {

  hasRemainingNewTopics =
    false;


  newTopicQueues.forEach(
    queue => {

      if (
        queue.items.length === 0
      ) {
        return;
      }


      const slot =
        queue.items.shift();


      newTopicSlots.push(
        slot
      );


      hasRemainingNewTopics =
        true;
    }
  );
}


// ====================================================
// E2. YENİ KONULARI ÖNCE TAKVİME YAY
// ====================================================

const schedulerStrategy =
  getSmartPlanStrategy(
    config.strategyId
  );


const minimumFollowUpWorkingDays =
  Math.max(
    0,
    Math.floor(
      Number(
        schedulerStrategy
          ?.newTopicPacing
          ?.minimumFollowUpWorkingDays ||
        0
      )
    )
  );


// NEW_TOPIC için kullanılabilecek son çalışma günü.
//
// Başlangıç:
// minimumFollowUpWorkingDays = 1
//
// Örn. 20 çalışma günü varsa:
// 0 ... 18 → NEW_TOPIC olabilir
// 19       → takip çalışması için korunur.
//
// Diğer stratejiler bu ayarı tanımlamadığı için
// mevcut davranışlarını aynen korur.
const lastUsableNewTopicGlobalIndex =
  Math.max(
    0,
    allDays.length -
    1 -
    minimumFollowUpWorkingDays
  );


const reservedFollowUpGlobalIndexes =
  allDays
    .filter(
      day =>
        day.globalIndex >
        lastUsableNewTopicGlobalIndex
    )
    .map(
      day =>
        day.globalIndex
    );


const lastNewTopicDayBySubject =
  {};


const lastNewTopicSlotBySubject =
  {};


// ----------------------------------------------------
// BAŞLANGIÇ — PRACTICE KAPASİTE REZERVASYONU
// ----------------------------------------------------
//
// Bir sonraki aynı-ders NEW_TOPIC açılmadan önce
// önceki öğrenme birimi için en az bir PRACTICE
// gerçekten sığabilecek kapasite korunur.
//
// Rezervasyon gerçek görevi takvime eklemez.
// Yalnız daha sonra yerleştirilecek NEW_TOPIC'lerin
// solver için gerekli PRACTICE kapasitesini
// tüketmesini engeller.
// ----------------------------------------------------

const reservedPracticeSlotsByGlobalIndex =
  new Map();


const getPracticeReservationAwareDay =
  day => {

    const reservedPracticeSlots =
      reservedPracticeSlotsByGlobalIndex
        .get(
          day?.globalIndex
        ) || [];


    if (
      reservedPracticeSlots.length === 0
    ) {
      return day;
    }


    return {
      ...day,

      tasks: [
        ...(day?.tasks || []),
        ...reservedPracticeSlots
      ]
    };
  };


// Strateji pacing'i bulunan planlarda her dersin
// kaçıncı NEW_TOPIC'inin yerleştirildiğini takip eder.
//
// Örn. Başlangıç + TYT Matematik:
//
// 0. konu → 1. hafta
// 1. konu → 2. hafta
// 2. konu → 3. hafta
// 3. konu → 4. hafta
//
// Böylece konu sayısını sınırlamakla kalmayıp
// gerçek ilerleme hızını da takvime yansıtırız.
const newTopicOrdinalBySubject =
  {};


newTopicSlots.forEach(
  (slot, index) => {

          // --------------------------------------------------
    // STRATEJİ BAZLI NEW_TOPIC HEDEFİ
    // --------------------------------------------------
    //
    // Pacing tanımlı stratejilerde yeni konuları
    // planın tamamına baştan sona orantılı yaymıyoruz.
    //
    // Örn:
    //
    // Başlangıç + TYT Matematik
    // haftada 1 yeni konu
    //
    // 4 haftalık plan:
    //
    // Konu 1 → 1. hafta başları
    // Konu 2 → 2. hafta başları
    // Konu 3 → 3. hafta başları
    // Konu 4 → 4. hafta başları
    //
    // Böylece yeni konunun ardından Practice / Review
    // için gerçek çalışma alanı bırakılır.
    // --------------------------------------------------

    const subjectKey =
      slot.subject ||
      '__unknown_subject__';


    const subjectOrdinal =
      Number(
        newTopicOrdinalBySubject[
          subjectKey
        ] || 0
      );


    newTopicOrdinalBySubject[
      subjectKey
    ] =
      subjectOrdinal + 1;


    const configuredNewTopicsPerWeek =
      Number(
        schedulerStrategy
          ?.newTopicPacing
          ?.subjectOverrides
          ?.[slot.subject] ??
        schedulerStrategy
          ?.newTopicPacing
          ?.defaultPerWeek ??
        0
      );


    const resolvedNewTopicsPerWeek =
      configuredNewTopicsPerWeek > 0
        ? Math.max(
            1,
            Math.floor(
              configuredNewTopicsPerWeek
            )
          )
        : 0;


    const workingDayCount =
      Math.max(
        1,
        Array.isArray(
          config.workingDays
        )
          ? config.workingDays.length
          : 1
      );


    let proportionalIndex;

let targetWeekStartGlobalIndex =
  null;


    if (
      resolvedNewTopicsPerWeek > 0
    ) {

      // Bu dersin yeni konusu hangi haftaya ait?
      const targetWeekOffset =
        Math.floor(
          subjectOrdinal /
          resolvedNewTopicsPerWeek
        );

      targetWeekStartGlobalIndex =
  targetWeekOffset *
  workingDayCount;


      // Aynı hafta birden fazla yeni konuya
      // izin verilen derslerde haftanın içine yay.
      //
      // Örn. Türkçe haftada 2:
      //
      // 1. konu → haftanın başı
      // 2. konu → haftanın ortaları
      const withinWeekOrdinal =
        subjectOrdinal %
        resolvedNewTopicsPerWeek;


      const targetDayPosition =
        Math.floor(
          (
            withinWeekOrdinal *
            workingDayCount
          ) /
          resolvedNewTopicsPerWeek
        );


      const pacedGlobalIndex =
        (
          targetWeekOffset *
          workingDayCount
        ) +
        targetDayPosition;


      proportionalIndex =
        Math.min(
          lastUsableNewTopicGlobalIndex,

          Math.max(
            0,
            pacedGlobalIndex
          )
        );
    }

    else {

      // Pacing tanımlamayan mevcut stratejilerin
      // eski davranışı aynen korunur.
      proportionalIndex =
        newTopicSlots.length > 1
          ? Math.round(
              (
                index /
                (
                  newTopicSlots.length -
                  1
                )
              ) *
              lastUsableNewTopicGlobalIndex
            )
          : 0;
    }


    let notBeforeGlobalIndex =
  null;


// Aynı dersin konu sırası korunur.
if (slot.subject) {

  const previous =
    lastNewTopicDayBySubject[
      slot.subject
    ];


  if (
    Number.isInteger(
      previous
    )
  ) {

    notBeforeGlobalIndex =
      previous;
  }
}

// --------------------------------------------------
// BAŞLANGIÇ — HEDEF HAFTADAN ÖNCEYE KAÇMA
// --------------------------------------------------
//
// Pacing'e göre örneğin 3. haftaya ait olan
// bir NEW_TOPIC, boş yer bulmak uğruna
// 2. veya 1. haftaya geri taşınamaz.
// --------------------------------------------------

if (
  config.strategyId ===
    'baslangic' &&
  resolvedNewTopicsPerWeek > 0 &&
  Number.isInteger(
    targetWeekStartGlobalIndex
  )
) {

  notBeforeGlobalIndex =
    Number.isInteger(
      notBeforeGlobalIndex
    )
      ? Math.max(
          notBeforeGlobalIndex,
          targetWeekStartGlobalIndex
        )
      : targetWeekStartGlobalIndex;
}




// --------------------------------------------------
// BAŞLANGIÇ — HAFTALIK NEW_TOPIC HARD LIMIT
// --------------------------------------------------
//
// Örn:
// TYT Matematik → max 1 / hafta
// TYT Geometri  → max 1 / hafta
// TYT Türkçe    → max 2 / hafta
//
// Bir haftada ilgili dersin kotası dolmuşsa,
// o haftanın tüm günleri bu NEW_TOPIC için
// aday listesinden çıkarılır.
//
// Slot kaybolmaz:
// sonraki uygun haftaya ilerleyebilir.
// --------------------------------------------------

const weeklyNewTopicCountByWeek =
  new Map();


if (
  config.strategyId ===
    'baslangic' &&
  resolvedNewTopicsPerWeek > 0 &&
  slot.subject
) {

  scheduledSlots
    .filter(
      scheduledSlot =>
        scheduledSlot?.type ===
          SMART_PLAN_TASK_TYPES.NEW_TOPIC &&
        scheduledSlot?.subject ===
          slot.subject &&
        Number.isInteger(
          Number(
            scheduledSlot?.weekNumber
          )
        )
    )
    .forEach(
      scheduledSlot => {

        const weekNumber =
          Number(
            scheduledSlot.weekNumber
          );


        weeklyNewTopicCountByWeek
          .set(
            weekNumber,

            Number(
              weeklyNewTopicCountByWeek
                .get(
                  weekNumber
                ) || 0
            ) + 1
          );
      }
    );
}


const fullNewTopicWeekNumbers =
  new Set(
    [
      ...weeklyNewTopicCountByWeek
        .entries()
    ]
      .filter(
        ([, count]) =>
          count >=
          resolvedNewTopicsPerWeek
      )
      .map(
        ([weekNumber]) =>
          weekNumber
      )
  );


const previousNewTopicSlot =
  slot.subject
    ? lastNewTopicSlotBySubject[
        slot.subject
      ] || null
    : null;


const gatePracticeSlot =
  (
    config.strategyId ===
      'baslangic' &&
    previousNewTopicSlot
      ?.learningSequenceRequired ===
        true &&
    previousNewTopicSlot
      ?.learningUnitId
  )
    ? (
        slots
          .filter(
            candidate =>
              candidate?.type ===
                SMART_PLAN_TASK_TYPES.PRACTICE &&
              candidate
                ?.learningSequenceRequired ===
                  true &&
              candidate?.learningUnitId ===
                previousNewTopicSlot
                  .learningUnitId
          )
          .sort(
            (a, b) =>
              Number(
                a?.duration || 0
              ) -
              Number(
                b?.duration || 0
              )
          )[0] ||
        null
      )
    : null;


const hasFeasiblePracticeGateBefore =
  candidateGlobalIndex => {

    if (
      !gatePracticeSlot ||
      !Number.isInteger(
        Number(
          previousNewTopicSlot
            ?.globalDayIndex
        )
      )
    ) {
      return true;
    }


    return allDays
      .some(
        candidateDay => {

          if (
            candidateDay.globalIndex <
              Number(
                previousNewTopicSlot
                  .globalDayIndex
              ) ||
            candidateDay.globalIndex >=
              candidateGlobalIndex
          ) {
            return false;
          }


          return canPlaceSmartPlanSlot({
            day:
              getPracticeReservationAwareDay(
                candidateDay
              ),

            slot:
              gatePracticeSlot,

            config
          });
        }
      );
  };


const practiceGateExcludedGlobalIndexes =
  gatePracticeSlot
    ? allDays
        .filter(
          candidateDay =>
            !hasFeasiblePracticeGateBefore(
              candidateDay.globalIndex
            )
        )
        .map(
          candidateDay =>
            candidateDay.globalIndex
        )
    : [];


const practiceReservationExcludedGlobalIndexes =
  allDays
    .filter(
      candidateDay => {

        const reservations =
          reservedPracticeSlotsByGlobalIndex
            .get(
              candidateDay.globalIndex
            ) || [];


        if (
          reservations.length === 0
        ) {
          return false;
        }


        return !canPlaceSmartPlanSlot({
          day:
            getPracticeReservationAwareDay(
              candidateDay
            ),

          slot,

          config
        });
      }
    )
    .map(
      candidateDay =>
        candidateDay.globalIndex
    );


const newTopicExcludedGlobalIndexes =
  [
    ...new Set([
      ...reservedFollowUpGlobalIndexes,

      ...practiceGateExcludedGlobalIndexes,

      ...practiceReservationExcludedGlobalIndexes,

      ...allDays
        .filter(
          day =>
            fullNewTopicWeekNumbers
              .has(
                day.weekNumber
              )
        )
        .map(
          day =>
            day.globalIndex
        )
    ])
  ];

       const day =
      findBestDayForSlot({
        allDays,
        slot,
        config,

        targetGlobalIndex:
          proportionalIndex,

        notBeforeGlobalIndex,

        excludedGlobalIndexes:
  newTopicExcludedGlobalIndexes
      });


    if (!day) {

      unscheduledSlots.push(
        createUnscheduledSlot({
          slot,

          reason:
            resolveUnscheduledReason({
              slot,
              config
            })
        })
      );

      return;
    }


    const placed =
      placeSlotOnDay({
        day,
        slot,
        scheduleOrder
      });


    scheduleOrder += 1;


    scheduledSlots.push(
      placed
    );


        // ------------------------------------------------
    // ÖNCEKİ KONU İÇİN GERÇEK PRACTICE KAPASİTESİNİ
    // BU NEW_TOPIC'TEN ÖNCE REZERVE ET
    // ----------------------------------------------------

    if (
      gatePracticeSlot &&
      previousNewTopicSlot &&
      Number.isInteger(
        Number(
          previousNewTopicSlot
            ?.globalDayIndex
        )
      )
    ) {

      const reservationCandidates =
        allDays
          .filter(
            candidateDay =>
              candidateDay.globalIndex >=
                Number(
                  previousNewTopicSlot
                    .globalDayIndex
                ) &&
              candidateDay.globalIndex <
                placed.globalDayIndex
          )
          .filter(
            candidateDay =>
              canPlaceSmartPlanSlot({
                day:
                  getPracticeReservationAwareDay(
                    candidateDay
                  ),

                slot:
                  gatePracticeSlot,

                config
              })
          )
          .map(
            candidateDay => {

              const reservationAwareDay =
                getPracticeReservationAwareDay(
                  candidateDay
                );


              const remainingAfterPlacement =
                Number(
                  config.dailyMinutes || 0
                ) -
                (
                  calculateDailyMinutes(
                    reservationAwareDay
                      .tasks
                  ) +
                  Number(
                    gatePracticeSlot
                      ?.duration || 0
                  )
                );


              return {
                day:
                  candidateDay,

                remainingAfterPlacement
              };
            }
          )
          .sort(
            (a, b) => {

              if (
                a.remainingAfterPlacement !==
                b.remainingAfterPlacement
              ) {
                return (
                  a.remainingAfterPlacement -
                  b.remainingAfterPlacement
                );
              }


              return (
                a.day.globalIndex -
                b.day.globalIndex
              );
            }
          );


      const reservationDay =
        reservationCandidates[0]
          ?.day ||
        null;


      if (
        reservationDay
      ) {

        const existingReservations =
          reservedPracticeSlotsByGlobalIndex
            .get(
              reservationDay
                .globalIndex
            ) || [];


        reservedPracticeSlotsByGlobalIndex
          .set(
            reservationDay
              .globalIndex,

            [
              ...existingReservations,
              gatePracticeSlot
            ]
          );
      }
    }


    if (slot.subject) {

      lastNewTopicDayBySubject[
        slot.subject
      ] =
        day.globalIndex;


      lastNewTopicSlotBySubject[
        slot.subject
      ] =
        placed;
    }
  }
);







// ====================================================
// E3. ESNEK ÇALIŞMALAR — GLOBAL SOLVER
// ====================================================
//
// Practice / Review / Mixed Practice / Routine
// görevleri artık tek tek greedy biçimde
// yerleştirilmez.
//
// Bunun yerine:
//
// 1. Kritik görevler ve NEW_TOPIC'ler sabit kalır.
// 2. Kalan tüm esnek görevler birlikte değerlendirilir.
// 3. En az seçeneği olan görev önce çözülür.
// 4. Bir tercih çıkmaza girerse geri dönülür.
// 5. Başka gün kombinasyonu denenir.
//
// Böylece:
//
// "Toplam kapasite var ama greedy algoritma
// yanlış yerleştirdiği için birkaç slot dışarıda kaldı"
//
// problemi ortadan kaldırılır.
//
// Math.random / Date.now kullanılmaz.
// Aynı girdi → aynı arama sırası → aynı sonuç.
// ====================================================


// ----------------------------------------------------
// 1. ÖĞRENME BİRİMİ AÇILIŞ GÜNLERİ
// ----------------------------------------------------
//
// NEW_TOPIC görevleri bu noktaya gelmeden önce
// takvime yerleştirilmiş durumdadır.
//
// Aynı learningUnitId'ye sahip PRACTICE görevlerinin
// kendi konu açılışından önce yerleşmesini engellemek
// için konu açılış gününü burada indeksliyoruz.
// ----------------------------------------------------

const learningIntroductionDayByUnitId =
  new Map();


scheduledSlots
  .filter(
    slot =>
      slot?.type ===
        SMART_PLAN_TASK_TYPES.NEW_TOPIC &&
      slot?.learningUnitId
  )
  .forEach(slot => {

    const unitId =
      slot.learningUnitId;


    const globalDayIndex =
      Number(
        slot?.globalDayIndex
      );


    if (
      !Number.isInteger(
        globalDayIndex
      )
    ) {
      return;
    }


    const existing =
      learningIntroductionDayByUnitId
        .get(unitId);


    if (
      !Number.isInteger(existing) ||
      globalDayIndex < existing
    ) {

      learningIntroductionDayByUnitId
        .set(
          unitId,
          globalDayIndex
        );
    }
   });


// ----------------------------------------------------
// 1.5. AYNI DERSTE SONRAKİ NEW_TOPIC GÜNÜ
// ----------------------------------------------------
//
// Başlangıç stratejisinde bir NOT_STARTED konu
// açıldıktan sonra aynı derste sonraki NEW_TOPIC
// açılmadan önce en az bir PRACTICE görülmelidir.
//
// NEW_TOPIC'ler solver'dan önce sabit takvime
// yerleştirildiği için her learningUnitId için
// sonraki aynı-ders NEW_TOPIC gününü burada
// deterministik olarak indeksliyoruz.
// ----------------------------------------------------

const nextIntroductionDayByUnitId =
  new Map();


if (
  config.strategyId ===
    'baslangic'
) {

  const introductionsBySubject =
    new Map();


  scheduledSlots
    .filter(
      slot =>
        slot?.type ===
          SMART_PLAN_TASK_TYPES.NEW_TOPIC &&
        slot?.learningSequenceRequired ===
          true &&
        slot?.learningUnitId &&
        slot?.subject &&
        Number.isInteger(
          Number(
            slot?.globalDayIndex
          )
        )
    )
    .forEach(slot => {

      if (
        !introductionsBySubject.has(
          slot.subject
        )
      ) {

        introductionsBySubject.set(
          slot.subject,
          []
        );
      }


      introductionsBySubject
        .get(
          slot.subject
        )
        .push(slot);
    });


  introductionsBySubject
    .forEach(subjectIntroductions => {

      const orderedIntroductions =
        [...subjectIntroductions]
          .sort(
            (a, b) => {

              const dayDifference =
                Number(
                  a.globalDayIndex
                ) -
                Number(
                  b.globalDayIndex
                );


              if (
                dayDifference !== 0
              ) {

                return dayDifference;
              }


              return (
                Number(
                  a.scheduleOrder || 0
                ) -
                Number(
                  b.scheduleOrder || 0
                )
              );
            }
          );


      for (
        let index = 0;
        index <
          orderedIntroductions.length - 1;
        index += 1
      ) {

        const currentIntroduction =
          orderedIntroductions[index];

        const nextIntroduction =
          orderedIntroductions[
            index + 1
          ];


        nextIntroductionDayByUnitId
          .set(
            currentIntroduction
              .learningUnitId,

            Number(
              nextIntroduction
                .globalDayIndex
            )
          );
      }
    });
}


// ====================================================
// E2.5. ZORUNLU İLK PRACTICE — DOĞRUDAN YERLEŞTİRME
// ====================================================
//
// Performans sözleşmesi:
//
// SlotBuilder bir PRACTICE'i:
//
// mandatoryFirstPractice: true
//
// olarak işaretlerse bu görev global backtracking
// solver'a bırakılmaz.
//
// NEW_TOPIC zaten takvimde sabittir.
//
// Bu nedenle ilk gerçek uygulama:
//
// 1. kendi NEW_TOPIC gününden önceye gitmez,
// 2. ilk uygun çalışma gününe deterministik yerleşir,
// 3. hard placement kurallarının tamamını kullanır,
// 4. yerleşemezse solver'a geri bırakılır.
//
// ŞİMDİLİK SlotBuilder bu etiketi üretmediği için
// mevcut plan davranışını değiştirmez.
// ====================================================

const directlyScheduledMandatoryPracticeIds =
  new Set();


const directlyScheduledMandatoryPracticeDayByUnitId =
  new Map();


const mandatoryFirstPracticeSlots =
  slots.filter(
    slot =>
      slot?.type ===
        SMART_PLAN_TASK_TYPES.PRACTICE &&

      slot?.mandatoryFirstPractice ===
        true &&

      slot?.learningSequenceRequired ===
        true &&

      Boolean(
        slot?.learningUnitId
      )
  );


mandatoryFirstPracticeSlots.forEach(
  slot => {

    const introductionGlobalIndex =
      learningIntroductionDayByUnitId
        .get(
          slot.learningUnitId
        );


    if (
      !Number.isInteger(
        introductionGlobalIndex
      )
    ) {
      return;
    }


    const candidateDay =
      allDays
        .filter(
          day =>
            day.globalIndex >=
              introductionGlobalIndex
        )
        .filter(
          day =>
            canPlaceSmartPlanSlot({
              day,
              slot,
              config
            })
        )
        .sort(
          (a, b) =>
            a.globalIndex -
            b.globalIndex
        )[0] ||
      null;


    if (!candidateDay) {
      return;
    }


    const placed =
      placeSlotOnDay({
        day:
          candidateDay,

        slot,

        scheduleOrder
      });


    scheduleOrder += 1;


       scheduledSlots.push(
      placed
    );


    directlyScheduledMandatoryPracticeIds
      .add(
        slot.id
      );


    const existingDirectPracticeDay =
      directlyScheduledMandatoryPracticeDayByUnitId
        .get(
          slot.learningUnitId
        );


    if (
      !Number.isInteger(
        existingDirectPracticeDay
      ) ||
      candidateDay.globalIndex <
        existingDirectPracticeDay
    ) {

      directlyScheduledMandatoryPracticeDayByUnitId
        .set(
          slot.learningUnitId,
          candidateDay.globalIndex
        );
    }
  }
);


// ----------------------------------------------------
// 2. ESNEK SLOT HAVUZU
// ----------------------------------------------------

const flexibleStudySlots =
  interleaveStudySlots(
    slots.filter(
      slot =>
        isStudySlot(slot) &&

        slot.type !==
          SMART_PLAN_TASK_TYPES.NEW_TOPIC &&

        !directlyScheduledMandatoryPracticeIds
          .has(
            slot.id
          )
    )
  );



// ----------------------------------------------------
// PERF-01 — SAFE CANDIDATE SYMMETRY
// ----------------------------------------------------
//
// Flexible havuzda zaman bağımlı öğrenme
// zinciri YOKSA candidate symmetry güvenli biçimde
// kullanılabilir.
//
// learningSequenceRequired bulunan planlarda:
//
// - PRACTICE → NEW_TOPIC
// - REVIEW → PRACTICE
//
// gibi globalDayIndex bağımlılıkları vardır.
//
// Bu nedenle temporal dependency taşıyan planlarda
// farklı takvim günleri kronolojik konumdan bağımsız
// biçimde eşdeğer kabul EDİLMEZ.
//
// Temporal dependency taşımayan planlarda ise:
//
// - günlük dakika limiti
// - günlük görev sayısı
// - heavy-task limiti
// - aynı konu+tür tekrarı
//
// günün mevcut hard-state içeriğinden türetilebilir.
//
// Böylece hard-rule açısından eşdeğer günler
// ayrı solver dalları olarak tekrar araştırılmaz.
// ----------------------------------------------------

const safeCandidateSymmetryEnabled =
  !flexibleStudySlots.some(
    slot =>
      slot?.learningSequenceRequired ===
        true
  );

// ----------------------------------------------------
// 2. HER SLOT İÇİN YUMUŞAK HEDEF NOKTASI
// ----------------------------------------------------
//
// Solver'ın esas amacı tüm slotları geçerli
// biçimde yerleştirmektir.
//
// Ancak birden fazla geçerli çözüm varsa,
// görevlerin plan boyunca dağılması için
// hedef indeks bilgisi kullanılır.
// ----------------------------------------------------

const flexibleSolverItems =
  flexibleStudySlots.map(
    (slot, originalIndex) => {

      const targetGlobalIndex =
        flexibleStudySlots.length > 1
          ? Math.round(
              (
                originalIndex /
                (
                  flexibleStudySlots.length -
                  1
                )
              ) *
              Math.max(
                0,
                allDays.length - 1
              )
            )
          : 0;


      return {
        slot,
        originalIndex,
        targetGlobalIndex
      };
    }
  );



// ----------------------------------------------------
// 3. SOLVER SINIRI
// ----------------------------------------------------
//
// Backtracking kontrolsüz bırakılmaz.
//
// Normal planlarda çözümün çok daha erken
// bulunması beklenir.
//
// Bu yalnızca güvenlik üst sınırıdır.
// ----------------------------------------------------

const FLEXIBLE_SOLVER_MAX_NODES =
  50000;


let flexibleSolverNodeCount = 0;

let flexibleSolverLimitReached =
  false;


// ----------------------------------------------------
// PERF-01 — SOLVER DIAGNOSTICS
// ----------------------------------------------------
//
// Yalnız config.debugSolverDiagnostics === true
// olduğunda sayaç toplar.
//
// Scheduling davranışını DEĞİŞTİRMEZ.
// Candidate silmez / eklemez.
// Sıralamayı değiştirmez.
// ----------------------------------------------------

const flexibleSolverDiagnosticsEnabled =
  config?.debugSolverDiagnostics === true;


const flexibleSolverDiagnostics = {

  candidateCalls:
    0,

  rawCandidateCount:
    0,

  postSymmetryCandidateCount:
    0,

  symmetryRemovedCount:
    0,

  candidateCallsByType:
    {},

  rawCandidatesByType:
    {},

  postSymmetryCandidatesByType:
    {},

  selectedByType:
    {},

  zeroCandidateDeadEndsByType:
    {},

  branchAttempts:
    0,

  backtrackCount:
    0,

  cacheHitCount:
    0,

  capacityPruneCount:
    0,

  maxDepth:
    0
};


const incrementFlexibleDiagnosticBucket = (
  bucket,
  key,
  amount = 1
) => {

  if (
    !flexibleSolverDiagnosticsEnabled
  ) {
    return;
  }


  const normalizedKey =
    key || 'UNKNOWN';


  bucket[normalizedKey] =
    Number(
      bucket[normalizedKey] || 0
    ) +
    Number(amount || 0);
};


// ----------------------------------------------------
// SOLVER STATE CACHE
// ----------------------------------------------------
//
// Daha önce çözümsüz olduğu kanıtlanmış aynı duruma
// tekrar gelinirse bütün alt ağacı yeniden aramayız.
//
// ÖNEMLİ:
// Solver artık zamana bağlı hard constraint'ler taşır:
//
// - PRACTICE kendi NEW_TOPIC'inden önce gelemez
// - REVIEW, PRACTICE'tan sonra gelmelidir
// - en az bir PRACTICE, sonraki aynı-ders
//   NEW_TOPIC'inden önce gerçekleşmelidir
//
// Bu nedenle aynı görev içeriklerinin farklı takvim
// günlerinde bulunması artık eşdeğer solver state'i
// değildir. Cache anahtarı kronolojik gün konumunu
// korumalıdır.
// ----------------------------------------------------

const failedFlexibleStates =
  new Set();


const getFlexibleConstraintSignature =
  slot => {

    return JSON.stringify([
      slot?.type || null,
      slot?.subject || null,
      slot?.topic || null,
      slot?.learningUnitId || null,
      Number(
        slot?.duration || 0
      )
    ]);
  };


const getDayConstraintSignature =
  day => {

    const taskSignatures =
      (day?.tasks || [])
        .map(
          getFlexibleConstraintSignature
        )
        .sort();


    return JSON.stringify([
      Number(
        day?.globalIndex ?? -1
      ),
      taskSignatures
    ]);
  };



// ----------------------------------------------------
// PERF-01 — CANDIDATE HARD-STATE SIGNATURE
// ----------------------------------------------------
//
// STATE CACHE signature'dan bilinçli olarak ayrıdır.
//
// Burada globalIndex YOK.
//
// Yalnız safeCandidateSymmetryEnabled === true
// olduğunda candidate dallarını eşdeğer
// saymak için kullanılır.
//
// Günün hard yerleştirme durumunu:
//
// - mevcut görev türleri
// - subject / topic
// - learningUnit
// - süre
//
// üzerinden temsil eder.
//
// Candidate sırası zaten önceden soft hedeflere göre
// sıralandığı için eşdeğer grubun ilk ve en iyi adayı
// korunur.
// ----------------------------------------------------

const getDayHardConstraintSignature =
  day => {

    const taskSignatures =
      (day?.tasks || [])
        .map(
          getFlexibleConstraintSignature
        )
        .sort();


    return JSON.stringify(
      taskSignatures
    );
  };


const buildFlexibleStateKey =
  remainingItems => {

    const remainingKey =
      remainingItems
        .map(
          item =>
            getFlexibleConstraintSignature(
              item.slot
            )
        )
        .sort();


    // Günlerin kronolojik sırası hard constraint'lerin
    // bir parçasıdır. Bu nedenle burada sort YOK.
    const dayKey =
      allDays
        .map(
          getDayConstraintSignature
        );


    return JSON.stringify([
      remainingKey,
      dayKey
    ]);
  };
// ----------------------------------------------------
// 4. GEÇİCİ / EN İYİ ATAMALAR
// ----------------------------------------------------

const currentFlexibleAssignments =
  new Map();


let bestFlexibleAssignments =
  new Map();


// ----------------------------------------------------
// 5. ŞİMDİYE KADARKİ EN İYİ KISMİ ÇÖZÜMÜ SAKLA
// ----------------------------------------------------

const rememberBestFlexibleAssignment =
  () => {

    if (
      currentFlexibleAssignments.size >
      bestFlexibleAssignments.size
    ) {

      bestFlexibleAssignments =
        new Map(
          currentFlexibleAssignments
        );
    }
  };


// ----------------------------------------------------
// 5.5. REVIEW → PRACTICE HARD BAĞIMLILIĞI
// ----------------------------------------------------
//
// Başlangıç stratejisinde NOT_STARTED bir REVIEW,
// aynı learningUnitId için en az bir PRACTICE
// solver içinde gerçekten yerleştirilmeden
// aday gün üretemez.
//
// Böylece REVIEW kronolojisi post-solver repair'e
// bırakılmadan solver state'inin parçası olur.
// ----------------------------------------------------

const isPracticeDependentReview =
  item =>
    (
      config.strategyId ===
        'baslangic' ||
      config.strategyId ===
        'dengeli' ||
      config.strategyId ===
        'telafi' ||
      config.strategyId ===
        'genel_tekrar'
    ) &&
    item?.slot?.type ===
      SMART_PLAN_TASK_TYPES.REVIEW &&
    item?.slot
      ?.learningSequenceRequired ===
        true &&
    item?.slot
      ?.learningRequiresStage ===
        'practice' &&
    Boolean(
      item?.slot
        ?.learningUnitId
    );
const getAssignedPracticeDayForUnitId =
  learningUnitId => {

    let earliestPracticeDay =
      directlyScheduledMandatoryPracticeDayByUnitId
        .get(
          learningUnitId
        );


    if (
      !Number.isInteger(
        earliestPracticeDay
      )
    ) {

      earliestPracticeDay =
        null;
    }


    // ----------------------------------------------
    // GLOBAL SOLVER İÇİNDE YERLEŞEN PRACTICE
    // ----------------------------------------------
    flexibleStudySlots
      .filter(
        slot =>
          slot?.type ===
            SMART_PLAN_TASK_TYPES.PRACTICE &&

          slot?.learningUnitId ===
            learningUnitId
      )
      .forEach(
        slot => {

          const assignedDay =
            currentFlexibleAssignments
              .get(
                slot.id
              );


          if (
            !Number.isInteger(
              assignedDay
            )
          ) {
            return;
          }


          if (
            !Number.isInteger(
              earliestPracticeDay
            ) ||
            assignedDay <
              earliestPracticeDay
          ) {

            earliestPracticeDay =
              assignedDay;
          }
        }
      );


    return earliestPracticeDay;
  };

// ----------------------------------------------------
// 5.6. SONRAKİ NEW_TOPIC GEÇİŞ KAPISI
// ----------------------------------------------------
//
// Aynı learningUnitId için sonraki aynı-ders
// NEW_TOPIC günü varsa, o güne kadar en az bir
// PRACTICE solver içinde gerçekten yerleşmiş olmalı.
//
// Bir PRACTICE bu kapıyı sağladıktan sonra aynı
// konuya ait diğer PRACTICE görevleri normal esnek
// davranışına devam eder.
// ----------------------------------------------------

const hasAssignedPracticeBeforeNextIntroduction =
  learningUnitId => {

    const nextIntroductionDay =
      nextIntroductionDayByUnitId
        .get(
          learningUnitId
        );


    if (
      !Number.isInteger(
        nextIntroductionDay
      )
    ) {

      return true;
    }


    return flexibleStudySlots
      .filter(
        slot =>
          slot?.type ===
            SMART_PLAN_TASK_TYPES.PRACTICE &&
          slot?.learningUnitId ===
            learningUnitId
      )
      .some(slot => {

        const assignedDay =
          currentFlexibleAssignments
            .get(
              slot.id
            );


        return (
          Number.isInteger(
            assignedDay
          ) &&
          assignedDay <
            nextIntroductionDay
        );
      });
  };


// ----------------------------------------------------
// 6. BİR SLOT İÇİN UYGUN GÜNLERİ BUL
// ----------------------------------------------------

const getFlexibleSolverCandidates =
  item => {

    const dailyLimit =
      Number(
        config.dailyMinutes || 0
      );


    // ------------------------------------------------
    // ÖĞRENME ZİNCİRİ — INTRODUCTION BAĞIMLILIĞI
    // ------------------------------------------------
    //
    // Şimdilik yalnız:
    //
    // PRACTICE
    //   ↓
    // kendi NEW_TOPIC'inden önce gelemez.
    //
    // Aynı gün mümkündür; NEW_TOPIC zaten Scheduler
    // tarafından daha önce yerleştirildiği için
    // scheduleOrder açısından önce gelir.
    //
    // REVIEW ve sonraki NEW_TOPIC kapısı bu turda
    // özellikle değiştirilmez.
    // ------------------------------------------------

const requiresIntroduction =
  (
    config.strategyId ===
      'baslangic' ||
    config.strategyId ===
      'dengeli' ||
    config.strategyId ===
      'telafi' ||
    config.strategyId ===
      'genel_tekrar'
  ) &&
  item?.slot
    ?.learningSequenceRequired ===
      true &&
  item?.slot
    ?.learningRequiresStage ===
      'introduction' &&
  Boolean(
    item?.slot
      ?.learningUnitId
  );

        const requiredIntroductionGlobalIndex =
      requiresIntroduction
        ? learningIntroductionDayByUnitId
            .get(
              item.slot.learningUnitId
            )
        : null;




    const requiresPractice =
      isPracticeDependentReview(
        item
      );


       const requiredPracticeGlobalIndex =
      requiresPractice
        ? getAssignedPracticeDayForUnitId(
            item.slot.learningUnitId
          )
        : null;


    const nextIntroductionGlobalIndex =
      requiresIntroduction
        ? nextIntroductionDayByUnitId
            .get(
              item.slot.learningUnitId
            )
        : null;


    const mustSatisfyNextTopicGate =
      requiresIntroduction &&
      Number.isInteger(
        nextIntroductionGlobalIndex
      ) &&
      !hasAssignedPracticeBeforeNextIntroduction(
        item.slot.learningUnitId
      );


    // Bağımlılık zorunlu ama konu açılışı
    // takvimde bulunamıyorsa görevi rastgele
    // bir güne koymak yerine aday üretmiyoruz.

       if (
      requiresIntroduction &&
      !Number.isInteger(
        requiredIntroductionGlobalIndex
      )
    ) {

      return [];
    }


    // REVIEW için PRACTICE bağımlılığı varsa,
    // solver henüz aynı learningUnitId'ye ait
    // hiçbir PRACTICE yerleştirmediyse REVIEW
    // bu state'te aday üretemez.
    if (
      requiresPractice &&
      !Number.isInteger(
        requiredPracticeGlobalIndex
      )
    ) {

      return [];
    }


    const rankedCandidates =
      allDays
        .filter(day => {

                    if (
            requiresIntroduction &&
            day.globalIndex <
              requiredIntroductionGlobalIndex
          ) {

            return false;
          }




                    // Aynı derste sonraki NEW_TOPIC varsa,
          // kapı henüz sağlanmamışken bu PRACTICE
          // mutlaka sonraki NEW_TOPIC gününden önce
          // yerleşmelidir.
          if (
            mustSatisfyNextTopicGate &&
            day.globalIndex >=
              nextIntroductionGlobalIndex
          ) {

            return false;
          }


          // REVIEW, aynı learningUnitId için
          // solver içinde gerçekten yerleştirilmiş
          // ilk PRACTICE gününden SONRA olmalıdır.
          if (
            requiresPractice &&
            day.globalIndex <=
              requiredPracticeGlobalIndex
          ) {

            return false;
          }


          return canPlaceSmartPlanSlot({
            day,
            slot:
              item.slot,
            config
          });
        })
        .map(day => {

          const usedMinutes =
            calculateDailyMinutes(
              day.tasks
            );


          const remainingAfterPlacement =
            dailyLimit -
            (
              usedMinutes +
              Number(
                item.slot
                  ?.duration || 0
              )
            );


          const sameSubjectCount =
            countSubjectOnDay({
              day,
              subject:
                item.slot?.subject
            });


          const targetDistance =
  Math.abs(
    day.globalIndex -
    item.targetGlobalIndex
  );


// ------------------------------------------------
// DENGELİ — PRACTICE YAKINLIK ÖNCELİĞİ
// ------------------------------------------------
//
// Hard kural DEĞİL.
//
// PRACTICE için kendi NEW_TOPIC gününe yakın
// uygun günler önce denenir.
//
// Solver gerekirse daha geç günleri yine
// kullanabilir; aday gün silinmez.
// ------------------------------------------------

const balancedPracticeIntroductionDay =
  (
    config.strategyId ===
      'dengeli' &&
    item?.slot?.type ===
      SMART_PLAN_TASK_TYPES.PRACTICE &&
    item?.slot?.learningUnitId
  )
    ? learningIntroductionDayByUnitId
        .get(
          item.slot.learningUnitId
        )
    : null;


const balancedPracticeIsBeforeIntroduction =
  Number.isInteger(
    balancedPracticeIntroductionDay
  ) &&
  day.globalIndex <
    balancedPracticeIntroductionDay;


const balancedPracticeDelay =
  Number.isInteger(
    balancedPracticeIntroductionDay
  ) &&
  !balancedPracticeIsBeforeIntroduction
    ? (
        day.globalIndex -
        balancedPracticeIntroductionDay
      )
    : Number.MAX_SAFE_INTEGER;


return {
  day,
  remainingAfterPlacement,
  sameSubjectCount,
  targetDistance,
  balancedPracticeIsBeforeIntroduction,
  balancedPracticeDelay
};
        })
        .sort(
          (a, b) => {



           // ------------------------------------------------
// DENGELİ PRACTICE — NEW_TOPIC'A YAKIN GÜNÜ ÖNCE DENE
// ------------------------------------------------

if (
  config.strategyId ===
    'dengeli' &&
  item?.slot?.type ===
    SMART_PLAN_TASK_TYPES.PRACTICE
) {
  // NEW_TOPIC öncesindeki günleri yasaklamıyoruz;
  // yalnızca en sona atıyoruz.
  if (
    a.balancedPracticeIsBeforeIntroduction !==
    b.balancedPracticeIsBeforeIntroduction
  ) {

    return a.balancedPracticeIsBeforeIntroduction
      ? 1
      : -1;
  }


  // NEW_TOPIC sonrasında en yakın günü
  // önce dene.
  if (
    a.balancedPracticeDelay !==
    b.balancedPracticeDelay
  ) {

    return (
      a.balancedPracticeDelay -
      b.balancedPracticeDelay
    );
  }
}

            // 1. BEST FIT
            if (
              a.remainingAfterPlacement !==
              b.remainingAfterPlacement
            ) {

              return (
                a.remainingAfterPlacement -
                b.remainingAfterPlacement
              );
            }


            // 2. AYNI DERSİ YIĞMAMA
            if (
              a.sameSubjectCount !==
              b.sameSubjectCount
            ) {

              return (
                a.sameSubjectCount -
                b.sameSubjectCount
              );
            }


            // 3. HEDEF NOKTAYA YAKINLIK
            if (
              a.targetDistance !==
              b.targetDistance
            ) {

              return (
                a.targetDistance -
                b.targetDistance
              );
            }


            // 4. DETERMINİSTİK EŞİTLİK
            return (
              a.day.globalIndex -
              b.day.globalIndex
            );
          }
        );


    // ------------------------------------------------
    // SYMMETRY BREAKING
    // ------------------------------------------------
    //
    // İki gün hard-rule açısından tamamen aynı
    // durumdaysa ikisini ayrı solver dalları olarak
    // araştırmanın anlamı yok.
    //
    // Sıralama önceden yapıldığı için eşdeğer
    // günlerden soft hedef açısından en iyi olan
    // korunur.
    // ------------------------------------------------

       const seenEquivalentDays =
      new Set();


    const symmetryFilteredCandidates =
      rankedCandidates.filter(
        candidate => {

                  const signature =
  safeCandidateSymmetryEnabled
    ? getDayHardConstraintSignature(
        candidate.day
      )
    : getDayConstraintSignature(
        candidate.day
      );


          if (
            seenEquivalentDays.has(
              signature
            )
          ) {

            return false;
          }


          seenEquivalentDays.add(
            signature
          );


          return true;
        }
      );


    if (
      flexibleSolverDiagnosticsEnabled
    ) {

      const slotType =
        item?.slot?.type ||
        'UNKNOWN';


      const rawCount =
        rankedCandidates.length;


      const postSymmetryCount =
        symmetryFilteredCandidates
          .length;


      flexibleSolverDiagnostics
        .candidateCalls += 1;


      flexibleSolverDiagnostics
        .rawCandidateCount +=
          rawCount;


      flexibleSolverDiagnostics
        .postSymmetryCandidateCount +=
          postSymmetryCount;


      flexibleSolverDiagnostics
        .symmetryRemovedCount +=
          Math.max(
            0,
            rawCount -
              postSymmetryCount
          );


      incrementFlexibleDiagnosticBucket(
        flexibleSolverDiagnostics
          .candidateCallsByType,

        slotType
      );


      incrementFlexibleDiagnosticBucket(
        flexibleSolverDiagnostics
          .rawCandidatesByType,

        slotType,

        rawCount
      );


      incrementFlexibleDiagnosticBucket(
        flexibleSolverDiagnostics
          .postSymmetryCandidatesByType,

        slotType,

        postSymmetryCount
      );
    }


    return symmetryFilteredCandidates;
  };

// ----------------------------------------------------
// 7. KALAN TOPLAM DAKİKA KAPASİTESİ
// ----------------------------------------------------

const hasEnoughFlexibleCapacity =
  remainingItems => {

    const dailyLimit =
      Number(
        config.dailyMinutes || 0
      );


    const dayCapacities =
      allDays.map(day => {

        const remainingMinutes =
          Math.max(
            0,

            dailyLimit -
            calculateDailyMinutes(
              day.tasks
            )
          );


        const remainingTaskSlots =
          Math.max(
            0,

            SMART_PLAN_LIMITS
              .maxTasksPerDay -
            day.tasks.length
          );


        return {
          day,
          remainingMinutes,
          remainingTaskSlots
        };
      });


    // ==================================================
    // 1. TOPLAM DAKİKA
    // ==================================================

    const requiredMinutes =
      remainingItems.reduce(
        (total, item) =>
          total +
          Number(
            item.slot
              ?.duration || 0
          ),
        0
      );


    const availableMinutes =
      dayCapacities.reduce(
        (total, capacity) =>
          total +
          capacity.remainingMinutes,
        0
      );


    if (
      availableMinutes <
      requiredMinutes
    ) {

      return false;
    }


    // ==================================================
    // 2. TOPLAM GÖREV POZİSYONU
    // ==================================================

    const availableTaskSlots =
      dayCapacities.reduce(
        (total, capacity) =>
          total +
          capacity.remainingTaskSlots,
        0
      );


    if (
      availableTaskSlots <
      remainingItems.length
    ) {

      return false;
    }


    // ==================================================
    // 3. SÜRE EŞİĞİ PRUNING
    // ==================================================
    //
    // Örn:
    //
    // Kalan görevler:
    // 45, 45, 30
    //
    // Ama 45+ dakika boşluğu olan yalnızca
    // tek uygun pozisyon varsa çözüm mümkün değildir.
    //
    // floor hesabı yalnızca üst sınırdır;
    // dolayısıyla yanlış negatif üretmez.
    // ==================================================

    const durationThresholds =
      [
        ...new Set(
          remainingItems
            .map(
              item =>
                Number(
                  item.slot
                    ?.duration || 0
                )
            )
            .filter(
              duration =>
                duration > 0
            )
        )
      ]
        .sort(
          (a, b) =>
            b - a
        );


    for (
      const threshold of
        durationThresholds
    ) {

      const requiredCount =
        remainingItems.filter(
          item =>
            Number(
              item.slot
                ?.duration || 0
            ) >= threshold
        ).length;


      const possibleCount =
        dayCapacities.reduce(
          (total, capacity) => {

            const byMinutes =
              Math.floor(
                capacity
                  .remainingMinutes /
                threshold
              );


            const possibleHere =
              Math.min(
                byMinutes,
                capacity
                  .remainingTaskSlots
              );


            return (
              total +
              possibleHere
            );
          },
          0
        );


      if (
        possibleCount <
        requiredCount
      ) {

        return false;
      }
    }


    

    return true;
  };



// ----------------------------------------------------
// 8. BACKTRACKING SOLVER
// ----------------------------------------------------
//
// MRV:
// "Minimum Remaining Values"
//
// Her adımda en az uygun günü bulunan görevi
// önce çözmeye çalışıyoruz.
//
// Böylece zor görevlerin sona kalıp
// takvimi kilitleme ihtimali azaltılır.
// ----------------------------------------------------

const solveFlexibleSchedule =
  remainingItems => {

        rememberBestFlexibleAssignment();


    if (
      flexibleSolverDiagnosticsEnabled
    ) {

      flexibleSolverDiagnostics
        .maxDepth =
          Math.max(
            flexibleSolverDiagnostics
              .maxDepth,

            currentFlexibleAssignments
              .size
          );
    }


    // ==================================================
    // TAM ÇÖZÜM
    // ==================================================

    if (
      remainingItems.length === 0
    ) {

      return true;
    }


    // ==================================================
    // ARAMA GÜVENLİK SINIRI
    // ==================================================

    if (
      flexibleSolverNodeCount >=
      FLEXIBLE_SOLVER_MAX_NODES
    ) {

      flexibleSolverLimitReached =
        true;

      return false;
    }


    flexibleSolverNodeCount += 1;


    // ==================================================
    // STATE CACHE
    // ==================================================

    const stateKey =
      buildFlexibleStateKey(
        remainingItems
      );


        if (
      failedFlexibleStates.has(
        stateKey
      )
    ) {

      if (
        flexibleSolverDiagnosticsEnabled
      ) {

        flexibleSolverDiagnostics
          .cacheHitCount += 1;
      }


      return false;
    }


    // ==================================================
    // GÜÇLÜ FEASIBILITY PRUNING
    // ==================================================

       if (
      !hasEnoughFlexibleCapacity(
        remainingItems
      )
    ) {

      if (
        flexibleSolverDiagnosticsEnabled
      ) {

        flexibleSolverDiagnostics
          .capacityPruneCount += 1;
      }


      failedFlexibleStates.add(
        stateKey
      );

      return false;
    }


    // ==================================================
    // EN KISITLI GÖREVİ SEÇ — MRV
    // ==================================================

    let selectedItemIndex =
      -1;

    let selectedCandidates =
      null;


    for (
      let index = 0;
      index <
        remainingItems.length;
      index += 1
    ) {

            const item =
        remainingItems[index];


      // PRACTICE bağımlı bir REVIEW,
      // aynı learningUnitId için henüz solver
      // içerisinde PRACTICE yerleştirilmediyse
      // MRV seçimine alınmaz.
      //
      // Önce prerequisite PRACTICE çözülür;
      // REVIEW daha sonraki recursion state'inde
      // gerçek PRACTICE gününü hard alt sınır
      // olarak kullanır.
      if (
        isPracticeDependentReview(
          item
        ) &&
        !Number.isInteger(
          getAssignedPracticeDayForUnitId(
            item.slot.learningUnitId
          )
        )
      ) {

        continue;
      }


      const candidates =
        getFlexibleSolverCandidates(
          item
        );


      // Bu görev şu anda hiçbir güne
      // yerleşemiyorsa mevcut dal çözümsüzdür.


           if (
        candidates.length === 0
      ) {

        incrementFlexibleDiagnosticBucket(
          flexibleSolverDiagnostics
            .zeroCandidateDeadEndsByType,

          item?.slot?.type
        );


        failedFlexibleStates.add(
          stateKey
        );

        return false;
      }


      if (
        selectedCandidates === null
      ) {

        selectedItemIndex =
          index;

        selectedCandidates =
          candidates;

        continue;
      }


      // ------------------------------------------------
      // 1. DAHA AZ ADAYI OLAN GÖREV
      // ------------------------------------------------

      if (
        candidates.length <
        selectedCandidates.length
      ) {

        selectedItemIndex =
          index;

        selectedCandidates =
          candidates;

        continue;
      }


      if (
        candidates.length >
        selectedCandidates.length
      ) {

        continue;
      }

	




      // ------------------------------------------------
      // 2. DAHA UZUN GÖREV
      // ------------------------------------------------

      const currentDuration =
        Number(
          item.slot
            ?.duration || 0
        );


      const selectedDuration =
        Number(
          remainingItems[
            selectedItemIndex
          ]?.slot
            ?.duration || 0
        );


      if (
        currentDuration >
        selectedDuration
      ) {

        selectedItemIndex =
          index;

        selectedCandidates =
          candidates;

        continue;
      }


      if (
        currentDuration <
        selectedDuration
      ) {

        continue;
      }


      // ------------------------------------------------
      // 3. ORİJİNAL SIRA
      // ------------------------------------------------

      if (
        item.originalIndex <
        remainingItems[
          selectedItemIndex
        ].originalIndex
      ) {

        selectedItemIndex =
          index;

        selectedCandidates =
          candidates;
      }
    }


    if (
      selectedItemIndex < 0 ||
      !selectedCandidates
    ) {

      failedFlexibleStates.add(
        stateKey
      );

      return false;
    }


        const selectedItem =
      remainingItems[
        selectedItemIndex
      ];


    incrementFlexibleDiagnosticBucket(
      flexibleSolverDiagnostics
        .selectedByType,

      selectedItem?.slot?.type
    );


    const nextItems = [
      ...remainingItems.slice(
        0,
        selectedItemIndex
      ),

      ...remainingItems.slice(
        selectedItemIndex + 1
      )
    ];


    // ==================================================
    // ADAY GÜNLERİ DENE
    // ==================================================

    for (
      const candidate of
        selectedCandidates
    ) {

            const day =
        candidate.day;


      if (
        flexibleSolverDiagnosticsEnabled
      ) {

        flexibleSolverDiagnostics
          .branchAttempts += 1;
      }


      // Geçici yerleştirme.
      day.tasks.push(
        selectedItem.slot
      );


      currentFlexibleAssignments.set(
        selectedItem.slot.id,
        day.globalIndex
      );


      const solved =
        solveFlexibleSchedule(
          nextItems
        );


      if (solved) {

        return true;
      }


           // BACKTRACK

      if (
        flexibleSolverDiagnosticsEnabled
      ) {

        flexibleSolverDiagnostics
          .backtrackCount += 1;
      }


      currentFlexibleAssignments.delete(
        selectedItem.slot.id
      );


      day.tasks.pop();


      // Arama sınırı gerçekten dolduysa
      // üst dallarda boşuna gezinme.
      if (
        flexibleSolverLimitReached
      ) {

        break;
      }
    }


    // ------------------------------------------------
    // Bu state tamamen araştırıldı ve çözüm yok.
    //
    // Arama limiti yüzünden yarıda kesildiyse
    // "çözümsüz" diye cache'e yazmıyoruz.
    // ------------------------------------------------

    if (
      !flexibleSolverLimitReached
    ) {

      failedFlexibleStates.add(
        stateKey
      );
    }


    return false;
  };

// ----------------------------------------------------
// 9. SOLVER'I ÇALIŞTIR
// ----------------------------------------------------

const flexibleScheduleSolved =
  solveFlexibleSchedule(
    flexibleSolverItems
  );

// ----------------------------------------------------
// 10. TAM ÇÖZÜM YOKSA EN İYİ KISMİ ÇÖZÜM
// ----------------------------------------------------

const finalFlexibleAssignments =
  flexibleScheduleSolved
    ? new Map(
        currentFlexibleAssignments
      )
    : new Map(
        bestFlexibleAssignments
      );


// ----------------------------------------------------
// 11. SOLVER'IN GEÇİCİ GÖREVLERİNİ TAKVİMDEN TEMİZLE
// ----------------------------------------------------
//
// Tam çözüm bulunduğunda recursion başarılı dalı
// day.tasks içinde bırakmış olabilir.
//
// Final schedule metadata ile yeniden oluşturulacak.
// ----------------------------------------------------

const flexibleSlotIds =
  new Set(
    flexibleStudySlots.map(
      slot =>
        slot.id
    )
  );


allDays.forEach(day => {

  day.tasks =
    day.tasks.filter(
      task =>
        !flexibleSlotIds.has(
          task?.id
        )
    );
});


// ----------------------------------------------------
// 11B. DAILY SUBJECT LIMIT CAUSAL DIAGNOSTIC
// ----------------------------------------------------
//
// Bir flexible slot takvime giremediyse:
//
// "maxSubjectsPerDay tanımlı"
// olması tek başına:
//
// DAILY_SUBJECT_LIMIT_CONSTRAINT
//
// demek için yeterli değildir.
//
// Görev:
//
// - günlük dakika,
// - görev sayısı,
// - heavy-task,
// - assessment,
// - aynı slot,
// - diğer hard kurallar
//
// nedeniyle de yerleşememiş olabilir.
//
// Bu nedenle aynı kısmi final takvimi iki kez kontrol ederiz:
//
// 1. gerçek config ile
// 2. yalnız maxSubjectsPerDay kaldırılmış config ile
//
// Aynı gün:
//
// gerçek config ile RED,
// subject limiti kaldırılınca GREEN
//
// oluyorsa günlük ders çeşitliliği kuralının
// gerçekten nedensel blocker olduğunu kabul ederiz.
//
// Bu kontrol yalnız teşhis içindir.
// Takvimi değiştirmez.
// ----------------------------------------------------

const buildFlexibleDiagnosticDay =
  day => {

    const assignedFlexibleTasks =
      flexibleStudySlots
        .filter(
          candidateSlot =>
            finalFlexibleAssignments
              .get(
                candidateSlot.id
              ) ===
              day.globalIndex
        );


    return {
      ...day,

      tasks: [
        ...(day?.tasks || []),
        ...assignedFlexibleTasks
      ]
    };
  };


const isBlockedByDailySubjectLimit =
  slot => {

    const maxSubjectsPerDay =
      Number(
        config.maxSubjectsPerDay
      );


    if (
      !slot?.subject ||
      !Number.isInteger(
        maxSubjectsPerDay
      ) ||
      maxSubjectsPerDay <= 0
    ) {
      return false;
    }


    // Solver arama limitine çarptıysa
    // kesin neden atfetmeyelim.
    //
    // Çünkü o durumda bütün çözüm uzayının
    // araştırıldığını bilmiyoruz.
    if (
      flexibleSolverLimitReached
    ) {
      return false;
    }


    const relaxedConfig = {
      ...config,

      maxSubjectsPerDay:
        null
    };


    return allDays.some(
      day => {

        const diagnosticDay =
          buildFlexibleDiagnosticDay(
            day
          );


        const fitsWithCurrentLimit =
          canPlaceSmartPlanSlot({
            day:
              diagnosticDay,

            slot,

            config
          });


        // Gerçek config ile zaten sığıyorsa
        // subject limiti blocker değildir.
        if (
          fitsWithCurrentLimit
        ) {
          return false;
        }


        const fitsWithoutSubjectLimit =
          canPlaceSmartPlanSlot({
            day:
              diagnosticDay,

            slot,

            config:
              relaxedConfig
          });


        return (
          fitsWithoutSubjectLimit ===
          true
        );
      }
    );
  };


// ----------------------------------------------------
// 12. FINAL ATAMALARI GERÇEK TAKVİME YAZ
// ----------------------------------------------------
flexibleStudySlots.forEach(
  slot => {

    const globalDayIndex =
      finalFlexibleAssignments.get(
        slot.id
      );


   if (
  !Number.isInteger(
    globalDayIndex
  )
) {

  const unscheduledReason =
    flexibleSolverLimitReached
      ? 'SOLVER_SEARCH_LIMIT_REACHED'
      : isBlockedByDailySubjectLimit(
          slot
        )
        ? 'DAILY_SUBJECT_LIMIT_CONSTRAINT'
        : 'NO_FEASIBLE_FLEXIBLE_SCHEDULE';


  unscheduledSlots.push(
    createUnscheduledSlot({
      slot,

      reason:
        unscheduledReason
    })
  );

  return;
}


    const day =
      allDays.find(
        candidateDay =>
          candidateDay.globalIndex ===
          globalDayIndex
      );


    if (!day) {

      unscheduledSlots.push(
        createUnscheduledSlot({
          slot,

          reason:
            'SOLVER_ASSIGNMENT_DAY_NOT_FOUND'
        })
      );

      return;
    }


    const placed =
      placeSlotOnDay({
        day,
        slot,
        scheduleOrder
      });


    scheduleOrder += 1;


    scheduledSlots.push(
      placed
    );
  }
);



// ====================================================
// E3.5. POST-SOLVER — DENGELİ GEÇ PRACTICE SWAP REPAIR
// ====================================================
//
// Amaç:
//
// Dengeli stratejide NEW_TOPIC sonrası ilk PRACTICE
// 3 çalışma gününü aşmışsa, global solver'ı yeniden
// çalıştırmadan yalnız lokal ve güvenli bir takas dener.
//
// Öncelik:
// 1. PRACTICE doğrudan izin verilen pencereye çekilebiliyorsa taşı.
// 2. Sığmıyorsa aynı süreli başka bir PRACTICE ile yer değiştir.
// 3. Donör PRACTICE'in REVIEW'u aynı güne düşecekse REVIEW'u
//    sonraki geçerli güne taşı.
//
// Başlangıç ve diğer stratejiler etkilenmez.
// ====================================================

if (
  config.strategyId ===
    'dengeli' &&
  flexibleScheduleSolved
) {

  const BALANCED_MAX_FIRST_PRACTICE_DELAY =
    3;


  const getScheduledDay =
    globalDayIndex =>
      allDays.find(
        day =>
          day.globalIndex ===
          globalDayIndex
      ) || null;


  const removeTaskFromDay = ({
    day,
    slotId
  }) => {

    if (!day) {
      return false;
    }


    const taskIndex =
      day.tasks.findIndex(
        task =>
          task?.id === slotId
      );


    if (taskIndex < 0) {
      return false;
    }


    day.tasks.splice(
      taskIndex,
      1
    );


    return true;
  };


  const updateScheduledSlotLocation = ({
    slot,
    day
  }) => {

    const updatedSlot = {
      ...slot,

      scheduled: true,

      weekNumber:
        day.weekNumber,

      day:
        day.day,

      dayPosition:
        day.dayPosition,

      globalDayIndex:
        day.globalIndex
    };


    const dayTaskIndex =
      day.tasks.findIndex(
        task =>
          task?.id === slot.id
      );


    if (
      dayTaskIndex >= 0
    ) {

      day.tasks[
        dayTaskIndex
      ] =
        updatedSlot;
    }


    const scheduledSlotIndex =
      scheduledSlots.findIndex(
        scheduledSlot =>
          scheduledSlot?.id ===
            slot.id
      );


    if (
      scheduledSlotIndex >= 0
    ) {

      scheduledSlots[
        scheduledSlotIndex
      ] =
        updatedSlot;
    }


    finalFlexibleAssignments.set(
      slot.id,
      day.globalIndex
    );


    return updatedSlot;
  };


  // --------------------------------------------------
  // 1. HER ÖĞRENME BİRİMİNİN İLK PRACTICE'INI BUL
  // --------------------------------------------------

  const firstPracticeByUnitId =
    new Map();


  scheduledSlots
    .filter(
      slot =>
        slot?.type ===
          SMART_PLAN_TASK_TYPES.PRACTICE &&
        slot?.learningUnitId &&
        Number.isInteger(
          Number(
            slot?.globalDayIndex
          )
        )
    )
    .forEach(slot => {

      const unitId =
        slot.learningUnitId;


      const existing =
        firstPracticeByUnitId
          .get(unitId);


      if (
        !existing ||
        Number(
          slot.globalDayIndex
        ) <
        Number(
          existing.globalDayIndex
        )
      ) {

        firstPracticeByUnitId
          .set(
            unitId,
            slot
          );
      }
    });


  const lateFirstPractices =
    [
      ...firstPracticeByUnitId
        .values()
    ]
      .filter(practice => {

        const introductionDay =
          learningIntroductionDayByUnitId
            .get(
              practice.learningUnitId
            );


        if (
          !Number.isInteger(
            introductionDay
          )
        ) {
          return false;
        }


        return (
          Number(
            practice.globalDayIndex
          ) -
          introductionDay >
          BALANCED_MAX_FIRST_PRACTICE_DELAY
        );
      })
      .sort(
        (a, b) => {

          const introA =
            learningIntroductionDayByUnitId
              .get(
                a.learningUnitId
              );

          const introB =
            learningIntroductionDayByUnitId
              .get(
                b.learningUnitId
              );


          const delayA =
            Number(
              a.globalDayIndex
            ) -
            Number(introA);

          const delayB =
            Number(
              b.globalDayIndex
            ) -
            Number(introB);


          return delayB - delayA;
        }
      );


  // --------------------------------------------------
  // 2. GEÇ PRACTICE'LARI TEK TEK LOKAL TAMİR ET
  // --------------------------------------------------

  lateFirstPractices
    .forEach(originalLatePractice => {

      const latePractice =
        scheduledSlots.find(
          slot =>
            slot?.id ===
              originalLatePractice.id
        ) ||
        originalLatePractice;


      const introductionDay =
        learningIntroductionDayByUnitId
          .get(
            latePractice.learningUnitId
          );


      if (
        !Number.isInteger(
          introductionDay
        )
      ) {
        return;
      }


      const currentPracticeDayIndex =
        Number(
          latePractice.globalDayIndex
        );


      const latestAllowedDayIndex =
        introductionDay +
        BALANCED_MAX_FIRST_PRACTICE_DELAY;


      if (
        currentPracticeDayIndex <=
        latestAllowedDayIndex
      ) {
        return;
      }


      const originalDay =
        getScheduledDay(
          currentPracticeDayIndex
        );


      if (!originalDay) {
        return;
      }


      const candidateTargetDays =
        allDays
          .filter(
            day =>
              day.globalIndex >=
                introductionDay &&
              day.globalIndex <=
                latestAllowedDayIndex &&
              day.globalIndex <
                currentPracticeDayIndex
          )
          .sort(
            (a, b) =>
              b.globalIndex -
              a.globalIndex
          );


      let repaired = false;


      for (
        const targetDay of
          candidateTargetDays
      ) {

        // --------------------------------------------
        // 2A. DOĞRUDAN TAŞIMA MÜMKÜN MÜ?
        // --------------------------------------------

        if (
          canPlaceSmartPlanSlot({
            day:
              targetDay,

            slot:
              latePractice,

            config
          })
        ) {

          if (
            !removeTaskFromDay({
              day:
                originalDay,

              slotId:
                latePractice.id
            })
          ) {
            continue;
          }


          targetDay.tasks.push(
            latePractice
          );


          updateScheduledSlotLocation({
            slot:
              latePractice,

            day:
              targetDay
          });


          repaired = true;
          break;
        }


        // --------------------------------------------
        // 2B. AYNI SÜRELİ PRACTICE DONÖRÜ BUL
        // --------------------------------------------

        const donorCandidates =
          targetDay.tasks
            .filter(
              task =>
                task?.id !==
                  latePractice.id &&
                task?.type ===
                  SMART_PLAN_TASK_TYPES.PRACTICE &&
                task?.learningUnitId &&
                Number(
                  task?.duration || 0
                ) ===
                Number(
                  latePractice?.duration || 0
                )
            )
            .map(donor => {

              const donorIntroductionDay =
                learningIntroductionDayByUnitId
                  .get(
                    donor.learningUnitId
                  );


              const blockingReviewCount =
                scheduledSlots.filter(
                  slot =>
                    slot?.type ===
                      SMART_PLAN_TASK_TYPES.REVIEW &&
                    slot?.learningUnitId ===
                      donor.learningUnitId &&
                    Number(
                      slot?.globalDayIndex
                    ) <=
                    currentPracticeDayIndex
                ).length;


              return {
                donor,
                donorIntroductionDay,
                blockingReviewCount
              };
            })
            .filter(candidate => {

              if (
                !Number.isInteger(
                  candidate
                    .donorIntroductionDay
                )
              ) {
                return false;
              }


              const donorDelayAfterSwap =
                currentPracticeDayIndex -
                candidate
                  .donorIntroductionDay;


              return (
                donorDelayAfterSwap >= 0 &&
                donorDelayAfterSwap <=
                  BALANCED_MAX_FIRST_PRACTICE_DELAY
              );
            })
            .sort(
              (a, b) => {

                if (
                  a.blockingReviewCount !==
                  b.blockingReviewCount
                ) {

                  return (
                    a.blockingReviewCount -
                    b.blockingReviewCount
                  );
                }


                return (
                  b.donorIntroductionDay -
                  a.donorIntroductionDay
                );
              }
            );


        for (
          const donorCandidate of
            donorCandidates
        ) {

          const donor =
            donorCandidate.donor;


          const blockingReviews =
            scheduledSlots
              .filter(
                slot =>
                  slot?.type ===
                    SMART_PLAN_TASK_TYPES.REVIEW &&
                  slot?.learningUnitId ===
                    donor.learningUnitId &&
                  Number(
                    slot?.globalDayIndex
                  ) <=
                    currentPracticeDayIndex
              )
              .sort(
                (a, b) =>
                  Number(
                    a?.globalDayIndex || 0
                  ) -
                  Number(
                    b?.globalDayIndex || 0
                  )
              );


          const dayTaskSnapshots =
            new Map(
              allDays.map(
                day => [
                  day.globalIndex,
                  [...day.tasks]
                ]
              )
            );


          const restoreDayTasks =
            () => {

              allDays.forEach(day => {

                day.tasks =
                  [
                    ...(
                      dayTaskSnapshots
                        .get(
                          day.globalIndex
                        ) ||
                      []
                    )
                  ];
              });
            };


          const lateRemoved =
            removeTaskFromDay({
              day:
                originalDay,

              slotId:
                latePractice.id
            });


          const donorRemoved =
            removeTaskFromDay({
              day:
                targetDay,

              slotId:
                donor.id
            });


          if (
            !lateRemoved ||
            !donorRemoved
          ) {

            restoreDayTasks();
            continue;
          }


          let blockingReviewsRemoved =
            true;


          blockingReviews.forEach(
            review => {

              const reviewDay =
                getScheduledDay(
                  Number(
                    review.globalDayIndex
                  )
                );


              if (
                !removeTaskFromDay({
                  day:
                    reviewDay,

                  slotId:
                    review.id
                })
              ) {

                blockingReviewsRemoved =
                  false;
              }
            }
          );


          if (
            !blockingReviewsRemoved ||
            !canPlaceSmartPlanSlot({
              day:
                targetDay,

              slot:
                latePractice,

              config
            }) ||
            !canPlaceSmartPlanSlot({
              day:
                originalDay,

              slot:
                donor,

              config
            })
          ) {

            restoreDayTasks();
            continue;
          }


          // Yeni kapasite durumunu simüle et.
          targetDay.tasks.push(
            latePractice
          );

          originalDay.tasks.push(
            donor
          );


          const reviewDestinations =
            new Map();


          let reviewRepairSucceeded =
            true;


          for (
            const review of
              blockingReviews
          ) {

            const repairedReviewDay =
              findBestDayForSlot({
                allDays,

                slot:
                  review,

                config,

                targetGlobalIndex:
                  Math.min(
                    Math.max(
                      0,
                      allDays.length - 1
                    ),
                    currentPracticeDayIndex + 1
                  ),

                notBeforeGlobalIndex:
                  currentPracticeDayIndex + 1
              });


            if (
              !repairedReviewDay
            ) {

              reviewRepairSucceeded =
                false;

              break;
            }


            repairedReviewDay.tasks.push(
              review
            );


            reviewDestinations.set(
              review.id,
              repairedReviewDay
            );
          }


          if (
            !reviewRepairSucceeded
          ) {

            restoreDayTasks();
            continue;
          }


          // ------------------------------------------
          // 2C. TAKASI KALICI HALE GETİR
          // ------------------------------------------

          updateScheduledSlotLocation({
            slot:
              latePractice,

            day:
              targetDay
          });


          updateScheduledSlotLocation({
            slot:
              donor,

            day:
              originalDay
          });


          blockingReviews.forEach(
            review => {

              const repairedReviewDay =
                reviewDestinations
                  .get(
                    review.id
                  );


              if (
                repairedReviewDay
              ) {

                updateScheduledSlotLocation({
                  slot:
                    review,

                  day:
                    repairedReviewDay
                });
              }
            }
          );


          repaired = true;
          break;
        }


        if (repaired) {
          break;
        }
      }
    });
}



// ====================================================
// E4. POST-SOLVER — LOKAL REVIEW KRONOLOJİ REPAIR
// ====================================================
//
// Global solver'ın kararlarını değiştirmiyoruz.
//
// Yalnız Başlangıç stratejisinde:
//
// NOT_STARTED bir konu için REVIEW,
// o konunun NEW_TOPIC gününden önce veya
// aynı gün yerleşmişse SADECE o REVIEW taşınır.
//
// Diğer bütün görevler olduğu yerde kalır.
// ====================================================

if (
  config.strategyId ===
  'baslangic'
) {

  // --------------------------------------------------
  // 1. KONU GİRİŞ GÜNLERİNİ BUL
  // --------------------------------------------------

  const introductionDayByTopicKey =
    new Map();


  scheduledSlots
    .filter(
      slot =>
        slot?.type ===
          SMART_PLAN_TASK_TYPES.NEW_TOPIC &&
        slot?.subject &&
        slot?.topic
    )
    .forEach(slot => {

      const key =
        `${slot.subject}__${slot.topic}`;


      const existingDay =
        introductionDayByTopicKey
          .get(key);


      if (
        !Number.isInteger(
          existingDay
        ) ||
        Number(
          slot.globalDayIndex
        ) <
        existingDay
      ) {

        introductionDayByTopicKey
          .set(
            key,
            Number(
              slot.globalDayIndex
            )
          );
      }
    });




  // --------------------------------------------------
  // 1.5. İLK GEÇERLİ PRACTICE GÜNÜNÜ BUL
  // --------------------------------------------------
  //
  // Yalnız plan içinde NEW_TOPIC olarak açılmış
  // NOT_STARTED konular için kullanılır.
  //
  // Aynı gün:
  // NEW_TOPIC → PRACTICE
  // mümkündür.
  //
  // Ancak REVIEW en az bu PRACTICE'ten sonraki
  // çalışma gününe taşınacaktır.
  // --------------------------------------------------

  const firstPracticeDayByTopicKey =
    new Map();


  scheduledSlots
    .filter(
      slot =>
        slot?.type ===
          SMART_PLAN_TASK_TYPES.PRACTICE &&
        slot?.topicStatus ===
          'not_started' &&
        slot?.subject &&
        slot?.topic
    )
    .forEach(slot => {

      const key =
        `${slot.subject}__${slot.topic}`;


      const introductionDay =
        introductionDayByTopicKey
          .get(key);


      const practiceDay =
        Number(
          slot.globalDayIndex
        );


      if (
        !Number.isInteger(
          introductionDay
        ) ||
        !Number.isInteger(
          practiceDay
        ) ||
        practiceDay <
          introductionDay
      ) {
        return;
      }


      const existingPracticeDay =
        firstPracticeDayByTopicKey
          .get(key);


      if (
        !Number.isInteger(
          existingPracticeDay
        ) ||
        practiceDay <
          existingPracticeDay
      ) {

        firstPracticeDayByTopicKey
          .set(
            key,
            practiceDay
          );
      }
    });


  // --------------------------------------------------
  // 2. HATALI REVIEW'LARI TESPİT ET
  // --------------------------------------------------

  const misplacedReviews =
    scheduledSlots
      .filter(slot => {

        if (
          slot?.type !==
            SMART_PLAN_TASK_TYPES.REVIEW ||
          slot?.topicStatus !==
            'not_started' ||
          !slot?.subject ||
          !slot?.topic
        ) {
          return false;
        }


        const key =
          `${slot.subject}__${slot.topic}`;


                const introductionDay =
          introductionDayByTopicKey
            .get(key);


        const firstPracticeDay =
          firstPracticeDayByTopicKey
            .get(key);


        if (
          !Number.isInteger(
            introductionDay
          ) ||
          !Number.isInteger(
            firstPracticeDay
          )
        ) {
          return false;
        }


        // REVIEW artık yalnız NEW_TOPIC'i değil,
        // ilk geçerli PRACTICE'i de beklemelidir.
        return (
          Number(
            slot.globalDayIndex
          ) <=
          firstPracticeDay
        );
            });

  // --------------------------------------------------
  // 3. YALNIZ HATALI REVIEW'U TAŞI
  // --------------------------------------------------

  misplacedReviews.forEach(
    review => {

      const topicKey =
        `${review.subject}__${review.topic}`;


           const introductionDay =
        introductionDayByTopicKey
          .get(topicKey);


      const firstPracticeDay =
        firstPracticeDayByTopicKey
          .get(topicKey);


      if (
        !Number.isInteger(
          introductionDay
        ) ||
        !Number.isInteger(
          firstPracticeDay
        )
      ) {
        return;
      }


      const originalDay =
        allDays.find(
          day =>
            day.globalIndex ===
            review.globalDayIndex
        );


      if (!originalDay) {
        return;
      }


      const originalTaskIndex =
        originalDay.tasks.findIndex(
          task =>
            task?.id ===
            review.id
        );


      if (
        originalTaskIndex < 0
      ) {
        return;
      }


      // Review'u geçici olarak eski gününden çıkar.
      // Böylece kendi dakikası aday gün hesabını
      // yanlış etkilemez.
      const [
        originalTask
      ] =
        originalDay.tasks.splice(
          originalTaskIndex,
          1
        );


      const reviewDelay =
        Math.max(
          1,
          Math.floor(
            Number(
              schedulerStrategy
                ?.repetition
                ?.firstReviewAfterDays ||
              1
            )
          )
        );


            // REVIEW, ilk geçerli PRACTICE'ten
      // sonraki çalışma gününden önce gelemez.
      const notBeforeGlobalIndex =
        firstPracticeDay + 1;


      // Stratejideki tekrar gecikmesini artık
      // PRACTICE üzerinden hedefliyoruz.
      //
      // Örn:
      // NEW_TOPIC  → Pazartesi
      // PRACTICE   → Çarşamba
      // REVIEW     → Çarşamba'dan sonraki
      //              uygun hedef noktası
      const targetGlobalIndex =
        Math.min(
          Math.max(
            0,
            allDays.length - 1
          ),

          firstPracticeDay +
            reviewDelay
        );


      const repairedDay =
        findBestDayForSlot({
          allDays,

          slot:
            review,

          config,

          targetGlobalIndex,

          notBeforeGlobalIndex,

          excludedGlobalIndexes: [
            originalDay.globalIndex
          ]
        });


      // Uygun yeni gün bulunamazsa mevcut planı
      // bozmuyoruz; görevi eski yerine geri koyuyoruz.
      if (!repairedDay) {

        originalDay.tasks.splice(
          originalTaskIndex,
          0,
          originalTask
        );

        return;
      }


      // Mevcut scheduleOrder korunur.
      //
      // Böylece yalnız takvim günü değişir;
      // diğer görevlerin sırası etkilenmez.
      const repairedReview =
        placeSlotOnDay({
          day:
            repairedDay,

          slot:
            review,

          scheduleOrder:
            review.scheduleOrder
        });


      const scheduledSlotIndex =
        scheduledSlots.findIndex(
          slot =>
            slot?.id ===
            review.id
        );


           if (
        scheduledSlotIndex >= 0
      ) {

        scheduledSlots[
          scheduledSlotIndex
        ] =
          repairedReview;
      }
    }
  );
}


// ====================================================
// E4.5. POST-SOLVER — AYNI KONU REVIEW ARALIĞI REPAIR
// ====================================================
//
// Yalnız Başlangıç stratejisinde:
//
// Aynı subject + topic için iki REVIEW
// ardışık çalışma günlerinde yer alamaz.
//
// Örnek:
//
// Cuma      → Sayılar REVIEW
// Cumartesi → Sayılar REVIEW   ❌
//
// En az bir çalışma günü aralık bırakılır:
//
// Cuma      → Sayılar REVIEW
// Cumartesi → başka çalışma
// Pazartesi → Sayılar REVIEW   ✅
//
// Bu katman:
//
// - REVIEW sayısını değiştirmez.
// - REVIEW silmez.
// - NEW_TOPIC / PRACTICE'e dokunmaz.
// - Günlük HARD LIMIT'i bozmaz.
// - Yalnız sorunlu olan sonraki REVIEW'u ileri taşır.
//
// ====================================================

if (
  config.strategyId ===
    'baslangic'
) {

  // --------------------------------------------------
  // 1. REVIEW'LARI KONU BAZINDA GRUPLA
  // --------------------------------------------------

  const reviewsByTopicKey =
    new Map();


  scheduledSlots
    .filter(
      slot =>
        slot?.type ===
          SMART_PLAN_TASK_TYPES.REVIEW &&
        slot?.subject &&
        slot?.topic &&
        Number.isInteger(
          Number(
            slot?.globalDayIndex
          )
        )
    )
    .forEach(review => {

      const key =
        `${review.subject}__${review.topic}`;


      if (
        !reviewsByTopicKey
          .has(key)
      ) {

        reviewsByTopicKey
          .set(
            key,
            []
          );
      }


      reviewsByTopicKey
        .get(key)
        .push(review);
    });


  // --------------------------------------------------
  // 2. AYNI KONUNUN REVIEW'LARINI TARİH SIRASINA KOY
  // --------------------------------------------------

  reviewsByTopicKey
    .forEach(reviews => {

      const orderedReviews =
        [
          ...reviews
        ]
          .sort(
            (a, b) => {

              const dayDifference =
                Number(
                  a?.globalDayIndex
                ) -
                Number(
                  b?.globalDayIndex
                );


              if (
                dayDifference !== 0
              ) {
                return dayDifference;
              }


              return (
                Number(
                  a?.scheduleOrder || 0
                ) -
                Number(
                  b?.scheduleOrder || 0
                )
              );
            }
          );


      let previousReview =
        null;


      orderedReviews
        .forEach(review => {

          if (
            !previousReview
          ) {

            previousReview =
              review;

            return;
          }


          const previousReviewDay =
            Number(
              previousReview
                ?.globalDayIndex
            );


          const currentReviewDay =
            Number(
              review
                ?.globalDayIndex
            );


          if (
            !Number.isInteger(
              previousReviewDay
            ) ||
            !Number.isInteger(
              currentReviewDay
            )
          ) {

            previousReview =
              review;

            return;
          }


          const reviewDayGap =
            currentReviewDay -
            previousReviewDay;


          // Zaten arada en az bir çalışma günü varsa
          // dokunma.
          if (
            reviewDayGap > 1
          ) {

            previousReview =
              review;

            return;
          }


          // ------------------------------------------------
          // 3. ARDIŞIK REVIEW'U ESKİ GÜNÜNDEN ÇIKAR
          // ------------------------------------------------

          const originalDay =
            allDays.find(
              day =>
                day.globalIndex ===
                currentReviewDay
            );


          if (!originalDay) {

            previousReview =
              review;

            return;
          }


          const originalTaskIndex =
            originalDay.tasks
              .findIndex(
                task =>
                  task?.id ===
                    review.id
              );


          if (
            originalTaskIndex < 0
          ) {

            previousReview =
              review;

            return;
          }


          const [
            originalTask
          ] =
            originalDay.tasks
              .splice(
                originalTaskIndex,
                1
              );


          // ------------------------------------------------
          // 4. EN AZ BİR ÇALIŞMA GÜNÜ ARA BIRAK
          // ------------------------------------------------
          //
          // previous = 34 ise:
          //
          // 35 yasak
          // 36 ve sonrası uygun
          //
          // Böylece global çalışma günü indeksinde
          // minimum fark 2 olur.
          // ------------------------------------------------

          const notBeforeGlobalIndex =
            previousReviewDay + 2;


          const targetGlobalIndex =
            Math.min(
              Math.max(
                0,
                allDays.length - 1
              ),

              notBeforeGlobalIndex
            );


          const repairedDay =
            findBestDayForSlot({
              allDays,

              slot:
                review,

              config,

              targetGlobalIndex,

              notBeforeGlobalIndex,

              excludedGlobalIndexes: [
                originalDay.globalIndex
              ]
            });


          // ------------------------------------------------
          // 5. UYGUN YER YOKSA PLANI BOZMA
          // ------------------------------------------------

          if (!repairedDay) {

            originalDay.tasks
              .splice(
                originalTaskIndex,
                0,
                originalTask
              );


            previousReview =
              review;

            return;
          }


          // ------------------------------------------------
          // 6. REVIEW'U YENİ GÜNE YERLEŞTİR
          // ------------------------------------------------

          const repairedReview =
            placeSlotOnDay({
              day:
                repairedDay,

              slot:
                review,

              scheduleOrder:
                review.scheduleOrder
            });


          // scheduledSlots içindeki eski metadata'yı da
          // yeni gün bilgileriyle değiştir.
          const scheduledSlotIndex =
            scheduledSlots
              .findIndex(
                slot =>
                  slot?.id ===
                    review.id
              );


          if (
            scheduledSlotIndex >= 0
          ) {

            scheduledSlots[
              scheduledSlotIndex
            ] =
              repairedReview;
          }


          // Bir sonraki REVIEW kontrolü artık
          // taşınmış gerçek günü esas alsın.
          previousReview =
            repairedReview;
        });
    });
}


// ----------------------------------------------------
// 13. SOLVER UYARILARI
// ----------------------------------------------------

if (
  flexibleSolverLimitReached &&
  !flexibleScheduleSolved
) {

  warnings.push({
    code:
      'FLEXIBLE_SOLVER_SEARCH_LIMIT_REACHED',

    visitedNodes:
      flexibleSolverNodeCount,

    maxNodes:
      FLEXIBLE_SOLVER_MAX_NODES,

    placedCount:
      finalFlexibleAssignments.size,

    totalCount:
      flexibleStudySlots.length
  });
}


if (
  !flexibleScheduleSolved &&
  !flexibleSolverLimitReached &&
  flexibleStudySlots.length > 0
) {

  warnings.push({
    code:
      'NO_COMPLETE_FLEXIBLE_SCHEDULE_FOUND',

    placedCount:
      finalFlexibleAssignments.size,

    totalCount:
      flexibleStudySlots.length
  });
}



if (
  flexibleSolverDiagnosticsEnabled
) {

  warnings.push({

        code:
      'FLEXIBLE_SOLVER_DIAGNOSTICS',

    safeCandidateSymmetryEnabled,

solved:
  flexibleScheduleSolved,

    limitReached:
      flexibleSolverLimitReached,

    visitedNodes:
      flexibleSolverNodeCount,

    maxNodes:
      FLEXIBLE_SOLVER_MAX_NODES,

    totalFlexibleCount:
      flexibleStudySlots.length,

    bestPlacedCount:
      finalFlexibleAssignments.size,

    failedStateCacheSize:
      failedFlexibleStates.size,

    candidateCalls:
      flexibleSolverDiagnostics
        .candidateCalls,

    rawCandidateCount:
      flexibleSolverDiagnostics
        .rawCandidateCount,

    postSymmetryCandidateCount:
      flexibleSolverDiagnostics
        .postSymmetryCandidateCount,

    symmetryRemovedCount:
      flexibleSolverDiagnostics
        .symmetryRemovedCount,

    candidateCallsByType:
      flexibleSolverDiagnostics
        .candidateCallsByType,

    rawCandidatesByType:
      flexibleSolverDiagnostics
        .rawCandidatesByType,

    postSymmetryCandidatesByType:
      flexibleSolverDiagnostics
        .postSymmetryCandidatesByType,

    selectedByType:
      flexibleSolverDiagnostics
        .selectedByType,

    zeroCandidateDeadEndsByType:
      flexibleSolverDiagnostics
        .zeroCandidateDeadEndsByType,

    branchAttempts:
      flexibleSolverDiagnostics
        .branchAttempts,

    backtrackCount:
      flexibleSolverDiagnostics
        .backtrackCount,

    cacheHitCount:
      flexibleSolverDiagnostics
        .cacheHitCount,

    capacityPruneCount:
      flexibleSolverDiagnostics
        .capacityPruneCount,

    maxDepth:
      flexibleSolverDiagnostics
        .maxDepth
  });
}




  // ====================================================
  // E2. SCOPED ASSESSMENT'LAR
  // ====================================================
  //
  // TOPIC_SCAN / WEEKLY_SCAN normal çalışma zinciri
  // yerleştirildikten SONRA takvime alınır.
  //
  // Bunun nedeni:
  //
  // - taramanın öğrenmeden önce görünmemesi,
  // - NEW_TOPIC / PRACTICE yerleşimini bozmaması,
  // - solver'a yeni temporal dependency yüklememek,
  // - mümkünse haftanın son bölümünde değerlendirme
  //   yapılmasıdır.
  //
  // SlotBuilder ileride assessmentTopics metadata'sını:
  //
  // [
  //   { subject, topic },
  //   ...
  // ]
  //
  // biçiminde gönderecektir.
  //
  // Scheduler bu kapsam içerisindeki planlanmış
  // çalışmaların en geç olduğu günü bulur ve
  // değerlendirmeyi ondan önceye koymaz.
  // ====================================================

  const scopedAssessmentSlots =
    slots.filter(
      slot =>
        slot?.type ===
          SMART_PLAN_TASK_TYPES.TOPIC_SCAN ||
        slot?.type ===
          SMART_PLAN_TASK_TYPES.WEEKLY_SCAN
    );


  


  scopedAssessmentSlots.forEach(
    slot => {

      const assessmentTopics =
        Array.isArray(
          slot?.assessmentTopics
        )
          ? slot.assessmentTopics
              .filter(
                item =>
                  item?.subject &&
                  item?.topic
              )
          : [];


      // ----------------------------------------------
      // KAPSAMSIZ SCOPED ASSESSMENT ÜRETİLMEMELİ
      // ----------------------------------------------

      if (
        assessmentTopics.length === 0
      ) {

        unscheduledSlots.push(
          createUnscheduledSlot({
            slot,

            reason:
              'ASSESSMENT_SCOPE_REQUIRED'
          })
        );

        return;
      }


      // ----------------------------------------------
      // ASSESSMENT KONU ANAHTARLARI
      // ----------------------------------------------

      const assessmentTopicKeys =
        new Set(
          assessmentTopics.map(
            item =>
              `${item.subject}__${item.topic}`
          )
        );


           // ----------------------------------------------
      // SCOPED ASSESSMENT — MİNİMUM ÖĞRENME ZİNCİRİ
      // ----------------------------------------------
      //
      // WEEKLY_SCAN bütün REVIEW / tekrar çalışmalarının
      // bitmesini beklemek zorunda değildir.
      //
      // Pedagojik minimum:
      //
      // NEW_TOPIC
      //      ↓
      // ilk gerçek PRACTICE
      //      ↓
      // assessment artık yapılabilir
      //
      // Her assessment konusu için:
      //
      // 1. schedule edilmiş NEW_TOPIC gününü bul
      // 2. ondan önce olmayan ilk PRACTICE gününü bul
      //
      // Sonra bu "ilk practice" günlerinin en geç olanını
      // assessment için kronolojik alt sınır yap.
      //
      // Böylece:
      //
      // - assessment öğrenmeden önce gelemez
      // - practice görmemiş konu ölçülemez
      // - sonraki REVIEW / ekstra PRACTICE görevleri
      //   assessment'ı gereksiz yere haftanın sonuna itmez
      // - global solver'a yeni temporal dependency eklenmez
      // ----------------------------------------------

      const scopedAssessmentDependencyRows =
        assessmentTopics.map(
          item => {

            const introductionGlobalIndex =
              scheduledSlots
                .filter(
                  task =>
                    task?.type ===
                      SMART_PLAN_TASK_TYPES.NEW_TOPIC &&

                    task?.subject ===
                      item.subject &&

                    task?.topic ===
                      item.topic &&

                    Number.isInteger(
                      task?.globalDayIndex
                    )
                )
                .reduce(
                  (earliest, task) => {

                    if (
                      !Number.isInteger(
                        earliest
                      )
                    ) {
                      return task.globalDayIndex;
                    }


                    return Math.min(
                      earliest,
                      task.globalDayIndex
                    );
                  },
                  null
                );


            const firstPracticeGlobalIndex =
              scheduledSlots
                .filter(
                  task =>
                    task?.type ===
                      SMART_PLAN_TASK_TYPES.PRACTICE &&

                    task?.subject ===
                      item.subject &&

                    task?.topic ===
                      item.topic &&

                    Number.isInteger(
                      task?.globalDayIndex
                    ) &&

                    Number.isInteger(
                      introductionGlobalIndex
                    ) &&

                    task.globalDayIndex >=
                      introductionGlobalIndex
                )
                .reduce(
                  (earliest, task) => {

                    if (
                      !Number.isInteger(
                        earliest
                      )
                    ) {
                      return task.globalDayIndex;
                    }


                    return Math.min(
                      earliest,
                      task.globalDayIndex
                    );
                  },
                  null
                );


            return {
              subject:
                item.subject,

              topic:
                item.topic,

              introductionGlobalIndex,

              firstPracticeGlobalIndex
            };
          }
        );


      // ----------------------------------------------
      // DEPENDENCY GERÇEKTEN SCHEDULE EDİLMİŞ Mİ?
      // ----------------------------------------------
      //
      // SlotBuilder PRACTICE üretmiş olsa bile,
      // scheduler o PRACTICE'ı yerleştirememiş olabilir.
      //
      // Böyle bir durumda assessment yapmak güvenli
      // değildir.
      // ----------------------------------------------

      const scopedAssessmentMissingDependencies =
        scopedAssessmentDependencyRows
          .filter(
            row =>
              !Number.isInteger(
                row.introductionGlobalIndex
              ) ||
              !Number.isInteger(
                row.firstPracticeGlobalIndex
              )
          );


      if (
        scopedAssessmentMissingDependencies
          .length > 0
      ) {

        unscheduledSlots.push(
          createUnscheduledSlot({
            slot,

            reason:
              'ASSESSMENT_DEPENDENCY_NOT_SCHEDULED'
          })
        );

        return;
      }


      // ----------------------------------------------
      // ASSESSMENT İÇİN EN ERKEN GÜN
      // ----------------------------------------------
      //
      // Her konunun ilk geçerli PRACTICE'ı tamamlanmış
      // olmalı.
      //
      // Daha sonraki REVIEW / ek practice çalışmaları
      // bu alt sınırı ileri taşımaz.
      // ----------------------------------------------

      const latestRequiredPracticeGlobalIndex =
        scopedAssessmentDependencyRows
          .reduce(
            (latest, row) =>
              Math.max(
                latest,
                row.firstPracticeGlobalIndex
              ),
            -1
          );


      // ----------------------------------------------
      // HEDEF HAFTA
      // ----------------------------------------------

      const targetWeek =
        Number.isInteger(
          Number(
            slot?.assessmentTargetWeek
          )
        ) &&
        Number(
          slot?.assessmentTargetWeek
        ) > 0
          ? Number(
              slot.assessmentTargetWeek
            )
          : Number(
              config.weeks
            );


      // Haftanın mümkün olduğunca son çalışma gününü
      // tercih et.
      const preferredDayPosition =
        Math.max(
          0,
          (
            config.workingDays
              ?.length || 1
          ) - 1
        );


      const day =
        findBestDayForSlot({

          allDays,

          slot,

          config,

          targetWeek,

          preferredDayPosition,

                   notBeforeGlobalIndex:
            latestRequiredPracticeGlobalIndex >= 0
              ? latestRequiredPracticeGlobalIndex
              : null
        });


      if (!day) {

        unscheduledSlots.push(
          createUnscheduledSlot({
            slot,

            reason:
              resolveUnscheduledReason({
                slot,
                config
              })
          })
        );

        return;
      }


      const placed =
        placeSlotOnDay({

          day,

          slot,

          scheduleOrder
        });


      scheduleOrder += 1;


      scheduledSlots.push(
        placed
      );
    }
  );





  // ====================================================
  // F. TANINMAYAN SLOT TÜRLERİ
  // ====================================================

  const handledIds =
    new Set(
      [
        ...scheduledSlots,
        ...unscheduledSlots
      ].map(
        slot => slot.id
      )
    );


    slots.forEach(slot => {

    if (
      !handledIds.has(
        slot.id
      )
    ) {

      unscheduledSlots.push(
        createUnscheduledSlot({
          slot,

          reason:
            'UNSUPPORTED_SLOT_TYPE'
        })
      );
    }
  });


  // ====================================================
  // G. KAPASİTE TOP-UP
  // ====================================================
  //
  // Normal plan tamamen yerleştirildikten sonra
  // çalışma günleri minimum kapasite bandına taşınır.
  //
  // Örnek:
  //
  // 120 dk hedef
  // → minimum 90 dk
  // → hard max 120 dk
  //
  // Top-up:
  //
  // - yalnız bütün normal slotlar yerleşmişse çalışır
  // - 30 dk bloklarla ilerler
  // - günlük hard limiti aşmaz
  // - maxTasksPerDay sınırını aşmaz
  // - mümkünse daha önce açılmış gerçek bir konuyu
  //   pekiştirme içeriği olarak kullanır
  //
  // PRACTICE / REVIEW zincirine yeni eleman
  // eklememek için görev tipi ROUTINE'dır.
  // ====================================================

  const dailyMinimumMinutes =
    getSmartPlanDailyMinimumMinutes(
      config.dailyMinutes
    );


  const topUpBlockMinutes =
    Number(
      SMART_PLAN_CAPACITY_POLICY
        ?.topUpBlockMinutes || 30
    );


  const MAX_TOP_UP_REUSE_PER_TOPIC =
    3;


  const topUpUseCountByTopic =
    new Map();


  const topUpAssignmentCountBySubject =
    new Map();


  const getTopUpTopicKey =
    slot =>
      `${slot?.subject}__${slot?.topic}`;


  // ----------------------------------------------------
  // DAHA ÖNCE AÇILMIŞ KONU HAVUZU
  // ----------------------------------------------------

  const introducedTopicSlots =
    scheduledSlots
      .filter(
        slot =>
          slot?.type ===
            SMART_PLAN_TASK_TYPES.NEW_TOPIC &&
          slot?.subject &&
          slot?.topic &&
          Number.isInteger(
            Number(
              slot?.globalDayIndex
            )
          )
      )
      .sort(
        (a, b) => {

          const dayDifference =
            Number(
              a.globalDayIndex
            ) -
            Number(
              b.globalDayIndex
            );


          if (
            dayDifference !== 0
          ) {
            return dayDifference;
          }


          return (
            Number(
              a.scheduleOrder || 0
            ) -
            Number(
              b.scheduleOrder || 0
            )
          );
        }
      );


  // ----------------------------------------------------
  // TOP-UP SLOTU ÜRET
  // ----------------------------------------------------

  const createCapacityTopUpSlot = ({
    day,
    blockIndex,
    sourceTopic = null
  }) => {

    const subject =
      sourceTopic?.subject ||
      null;


    const topic =
      sourceTopic?.topic ||
      null;


    const priority =
      subject
        ? (
            config
              ?.subjectPriorities
              ?.[subject] ||
            SMART_PLAN_PRIORITY_LEVELS
              .NORMAL
          )
        : SMART_PLAN_PRIORITY_LEVELS
            .NORMAL;


    return {

      id:
        `smart_top_up_${String(
          day.weekNumber
        ).padStart(
          2,
          '0'
        )}_${String(
          day.globalIndex
        ).padStart(
          3,
          '0'
        )}_${String(
          blockIndex
        ).padStart(
          2,
          '0'
        )}`,

      type:
        SMART_PLAN_TASK_TYPES.ROUTINE,

      subject,

      topic,

      duration:
        topUpBlockMinutes,

      difficulty:
        null,

      resource:
        null,

      questionCount:
        null,

      priority,

      priorityWeight:
        getSubjectPriorityWeight(
          priority
        ),

      topicStatus:
        sourceTopic?.topicStatus ||
        null,

      reason:
        subject && topic
          ? 'capacity_top_up_learned_topic'
          : 'capacity_top_up_general',

      scheduled:
        false,

      learningUnitId:
        subject && topic
          ? `${subject}__${topic}`
          : null,

      learningStage:
        'reinforcement',

      capacityTopUp:
        true
    };
  };


  // ----------------------------------------------------
  // UYGUN ÖĞRENİLMİŞ KONUYU SEÇ
  // ----------------------------------------------------

  const chooseTopUpTopic = ({
    day,
    blockIndex
  }) => {

    const candidates =
      introducedTopicSlots
        .filter(
          introduction => {

                       // Konu bu çalışma gününden önce
            // gerçekten açılmış olmalı.
            if (
              Number(
                introduction
                  .globalDayIndex
              ) >=
              Number(
                day.globalIndex
              )
            ) {
              return false;
            }


            // Top-up bir reinforcement görevidir.
            //
            // Bu nedenle yalnızca NEW_TOPIC görmüş
            // olmak yeterli değildir.
            //
            // Aynı konu, hedef top-up gününden önce
            // en az bir gerçek PRACTICE görmüş olmalıdır.
            const hasPriorPractice =
              scheduledSlots.some(
                scheduledSlot =>
                  scheduledSlot?.type ===
                    SMART_PLAN_TASK_TYPES.PRACTICE &&
                  scheduledSlot?.subject ===
                    introduction.subject &&
                  scheduledSlot?.topic ===
                    introduction.topic &&
                  Number.isInteger(
                    Number(
                      scheduledSlot
                        ?.globalDayIndex
                    )
                  ) &&
                  Number(
                    scheduledSlot
                      .globalDayIndex
                  ) >=
                    Number(
                      introduction
                        .globalDayIndex
                    ) &&
                  Number(
                    scheduledSlot
                      .globalDayIndex
                  ) <
                    Number(
                      day.globalIndex
                    )
              );


            if (
              !hasPriorPractice
            ) {
              return false;
            }


            const key =
              getTopUpTopicKey(
                introduction
              );


            const useCount =
              Number(
                topUpUseCountByTopic
                  .get(key) || 0
              );


            if (
              useCount >=
              MAX_TOP_UP_REUSE_PER_TOPIC
            ) {
              return false;
            }


            const candidateSlot =
              createCapacityTopUpSlot({
                day,
                blockIndex,
                sourceTopic:
                  introduction
              });


            return (
              canPlaceSmartPlanSlot({
                day,
                slot:
                  candidateSlot,
                config
              })
            );
          })
        .map(
          introduction => {

            const subject =
              introduction.subject;


            const priority =
              config
                ?.subjectPriorities
                ?.[subject] ||
              SMART_PLAN_PRIORITY_LEVELS
                .NORMAL;


            const weight =
              getSubjectPriorityWeight(
                priority
              );


            const subjectAssigned =
              Number(
                topUpAssignmentCountBySubject
                  .get(subject) || 0
              );


            const key =
              getTopUpTopicKey(
                introduction
              );


            const topicUseCount =
              Number(
                topUpUseCountByTopic
                  .get(key) || 0
              );


            return {
              introduction,

              key,

              weight,

              // Weighted fair allocation:
              //
              // very_high > priority > normal
              // fakat tek bir ders bütün top-up
              // havuzunu ele geçirmez.
              nextSubjectRatio:
                (
                  subjectAssigned + 1
                ) /
                Math.max(
                  1,
                  weight
                ),

              topicUseCount
            };
          }
        );


    candidates.sort(
      (a, b) => {

        if (
          a.nextSubjectRatio !==
          b.nextSubjectRatio
        ) {
          return (
            a.nextSubjectRatio -
            b.nextSubjectRatio
          );
        }


        if (
          a.topicUseCount !==
          b.topicUseCount
        ) {
          return (
            a.topicUseCount -
            b.topicUseCount
          );
        }


        if (
          a.weight !==
          b.weight
        ) {
          return (
            b.weight -
            a.weight
          );
        }


        const dayDifference =
          Number(
            a.introduction
              .globalDayIndex
          ) -
          Number(
            b.introduction
              .globalDayIndex
          );


        if (
          dayDifference !== 0
        ) {
          return dayDifference;
        }


        return (
          String(
            a.key
          ).localeCompare(
            String(
              b.key
            ),
            'tr'
          )
        );
      }
    );


    return (
      candidates[0]
        ?.introduction ||
      null
    );
  };


  // ----------------------------------------------------
  // GÜNLERİ TAMAMLA
  // ----------------------------------------------------
  //
  // Plan zaten kısmi / bozuksa top-up yapmıyoruz.
  //
  // Önce asıl görevlerin eksiksiz takvime
  // girmesi gerekir.
  // ----------------------------------------------------

  if (
    unscheduledSlots.length === 0 &&
    dailyMinimumMinutes > 0 &&
    topUpBlockMinutes > 0
  ) {

    [...allDays]
      .sort(
        (a, b) =>
          a.globalIndex -
          b.globalIndex
      )
      .forEach(day => {

        let blockIndex = 1;


        while (
          calculateDailyMinutes(
            day.tasks
          ) <
          dailyMinimumMinutes
        ) {

          if (
            day.tasks.length >=
            SMART_PLAN_LIMITS
              .maxTasksPerDay
          ) {
            break;
          }


          const sourceTopic =
  chooseTopUpTopic({
    day,
    blockIndex
  });


// --------------------------------------------------
// PEDAGOJİK TOP-UP GUARD
// --------------------------------------------------
//
// ROUTINE yalnız gerçek bir pekiştirme görevi olarak
// kullanılabilir.
//
// Uygun öğrenilmiş konu bulunamıyorsa:
//
// ❌ subject/topic'siz generic ROUTINE üretme
// ❌ sırf minimum dakika bandını doldurmak için
//    pedagojik içeriği olmayan görev ekleme
//
// Bunun yerine günü mevcut anlamlı yüküyle bırak.
//
// Kapasite, pedagojik gerekçenin önüne geçemez.
// --------------------------------------------------

if (
  !sourceTopic
) {
  break;
}


const topUpSlot =
  createCapacityTopUpSlot({
    day,
    blockIndex,
    sourceTopic
  });


// chooseTopUpTopic zaten adayın bu güne
// yerleşebilirliğini kontrol eder.
//
// Yine de state değişikliği / gelecekteki refactorlara
// karşı son savunma hattını koruyoruz.
if (
  !canPlaceSmartPlanSlot({
    day,
    slot:
      topUpSlot,
    config
  })
) {
  break;
}


          const placed =
            placeSlotOnDay({
              day,
              slot:
                topUpSlot,
              scheduleOrder
            });


          scheduleOrder += 1;


          scheduledSlots.push(
            placed
          );


          supplementalSlots.push(
            placed
          );


          if (
            sourceTopic
              ?.subject &&
            sourceTopic
              ?.topic
          ) {

            const key =
              getTopUpTopicKey(
                sourceTopic
              );


            topUpUseCountByTopic
              .set(
                key,

                Number(
                  topUpUseCountByTopic
                    .get(key) || 0
                ) + 1
              );


            topUpAssignmentCountBySubject
              .set(
                sourceTopic.subject,

                Number(
                  topUpAssignmentCountBySubject
                    .get(
                      sourceTopic.subject
                    ) || 0
                ) + 1
              );
          }


          blockIndex += 1;
        }
      });
  }


  // ====================================================
  // H. UYARILAR
  // ====================================================


  // ====================================================
  // G. UYARILAR
  // ====================================================

  if (
  unscheduledSlots.length > 0
) {

  warnings.push({
    code:
      'UNSCHEDULED_SLOTS',

    count:
      unscheduledSlots.length
  });
}


// ----------------------------------------------------
// DAILY SUBJECT LIMIT WARNING
// ----------------------------------------------------
//
// Slot bazlı nedensel teşhis yapıldıktan sonra
// UI'nın açıklama üretebilmesi için tek bir
// toplu warning oluşturulur.
// ----------------------------------------------------

const dailySubjectLimitBlockedSlots =
  unscheduledSlots.filter(
    slot =>
      slot?.unscheduledReason ===
        'DAILY_SUBJECT_LIMIT_CONSTRAINT'
  );


if (
  dailySubjectLimitBlockedSlots
    .length > 0
) {

  warnings.push({

    code:
      'DAILY_SUBJECT_LIMIT_CONSTRAINT',

    maxSubjectsPerDay:
      Number(
        config.maxSubjectsPerDay
      ),

    affectedCount:
      dailySubjectLimitBlockedSlots
        .length,

    affectedSubjects:
      [
        ...new Set(
          dailySubjectLimitBlockedSlots
            .map(
              slot =>
                slot?.subject
            )
            .filter(Boolean)
        )
      ]
  });
}


const tooLongSlots =
    unscheduledSlots.filter(
      slot =>
        slot.unscheduledReason ===
        'SLOT_EXCEEDS_DAILY_LIMIT'
    );


  if (
    tooLongSlots.length > 0
  ) {

    warnings.push({
      code:
        'SLOTS_EXCEED_DAILY_LIMIT',

      count:
        tooLongSlots.length,

      slotIds:
        tooLongSlots.map(
          slot => slot.id
        )
    });
  }


  // ====================================================
  // H. FINAL TAKVİM
  // ====================================================

  const finalSchedule =
    finalizeSchedule({
      schedule,
      config
    });


  const scheduledMinutes =
    scheduledSlots.reduce(
      (sum, slot) =>
        sum +
        Number(
          slot.duration || 0
        ),
      0
    );


  const unscheduledMinutes =
    unscheduledSlots.reduce(
      (sum, slot) =>
        sum +
        Number(
          slot.duration || 0
        ),
      0
    );


  const usedDayCount =
    finalSchedule.reduce(
      (total, week) =>
        total +
        week.days.filter(
          day =>
            day.taskCount > 0
        ).length,
      0
    );


  // ====================================================
  // I. SONUÇ
  // ====================================================

  return {
    success:
      unscheduledSlots.length === 0,

    schedule:
      finalSchedule,

        scheduledSlots,

    unscheduledSlots,

    supplementalSlots,

    warnings,

    summary: {

      totalSlots:
        slots.length +
        supplementalSlots.length,

      baseSlotCount:
        slots.length,

      supplementalCount:
        supplementalSlots.length,

      supplementalMinutes:
        supplementalSlots.reduce(
          (total, slot) =>
            total +
            Number(
              slot?.duration || 0
            ),
          0
        ),

      scheduledCount:
        scheduledSlots.length,

      unscheduledCount:
        unscheduledSlots.length,

      scheduledMinutes,

      unscheduledMinutes,

      totalWeekCount:
        Number(
          config.weeks || 0
        ),

      workingDayCount:
        Array.isArray(
          config.workingDays
        )
          ? config.workingDays.length
          : 0,

      totalCalendarDayCount:
        allDays.length,

      usedDayCount,

      dailyMinuteLimit:
        Number(
          config.dailyMinutes || 0
        )
    }
  };
};